import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import Avatar from '../components/Avatar';
import CKMailEditor from '../components/CKMailEditor';
import { sanitizeHtml, stripHtmlTags, formatMailDateTime, isInternalRecipient } from '../utils/sanitize';
import { 
  Search, 
  Mail, 
  Send, 
  Paperclip, 
  Download, 
  X, 
  ArrowLeft, 
  Star, 
  FileText, 
  Eye, 
  Save, 
  Trash2, 
  CheckCircle, 
  Plus, 
  UploadCloud,
  CheckCheck,
  Clock,
  ShieldCheck,
  Archive,
  FolderArchive,
  PackageOpen,
  FileArchive,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  CheckSquare,
  Square,
  ShieldAlert,
  Ban,
  MailOpen,
  MailCheck,
  Filter,
  AlertOctagon,
  GripVertical,
  PanelLeftClose,
  PanelLeft,
  RotateCcw,
  CornerUpLeft,
  CornerUpRight,
  Reply,
  ReplyAll,
  Forward,
  Sparkles,
  MessageSquare,
  Edit3,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  Layout,
  Columns,
  Maximize2,
  Loader2
} from 'lucide-react';
import { restoreFromZipBackup, exportMailBackupZip } from '../utils/emlBackupHelper';
import DocumentPreviewModal from '../components/DocumentPreviewModal';

const WebmailTab = ({ initialCompose }) => {
  const { 
    emails, 
    sendEmail, 
    currentUser, 
    getUserSignature, 
    users, 
    addCustomer, 
    customers,
    starredEmailIds,
    toggleStarEmail,
    drafts,
    saveDraft,
    deleteDraft,
    getUserStorageUsage,
    importRestoredEmails,
    groupMails,
    blockedSenders,
    blockSender,
    unblockSender,
    deleteEmails,
    moveEmailsToTrash,
    restoreEmailsFromTrash,
    markEmailsRead,
    reportSpamEmails,
    restoreSpamEmails,
    emptySpamFolder,
    fetchRemoteEmails,
    signatureFormat,
    sendKakaoBizMessage,
    formatKakaoTemplate,
    kakaoBizConfig,
    contracts
  } = useContext(AppContext);

  // Check if current user has an approved resignation
  const myApprovedResignation = (contracts || []).find(c => {
    if (!currentUser) return false;
    const myId = currentUser.UserID;
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myName = currentUser.Name;
    const isMine = (c.UserID && c.UserID === myId) || 
                   (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) || 
                   (c.UserName === myName);
    const isResig = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA');
    const isApproved = c.Status === 'APPROVED' || c.Status === 'SIGNED' || c.ResignationStatus === 'APPROVED';
    return isMine && isResig && isApproved;
  });

  const resignationDDayInfo = (() => {
    if (!myApprovedResignation) return null;
    const resigDateStr = myApprovedResignation.ResignationDate || myApprovedResignation.ContractEndDate;
    if (!resigDateStr) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(resigDateStr);
    target.setHours(0, 0, 0, 0);

    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let label = '';
    let badgeClass = '';
    let subMessage = '';

    if (diffDays > 0) {
      label = `D-${diffDays}`;
      badgeClass = 'bg-rose-500 text-white';
      subMessage = `퇴사 예정일(${resigDateStr})까지 ${diffDays}일 남았습니다.`;
    } else if (diffDays === 0) {
      label = 'D-Day';
      badgeClass = 'bg-rose-600 text-white animate-pulse';
      subMessage = `오늘(${resigDateStr})이 최종 퇴사일입니다.`;
    } else {
      label = `D+${Math.abs(diffDays)}`;
      badgeClass = 'bg-slate-600 text-white';
      subMessage = `퇴사일(${resigDateStr})이 경과하였습니다.`;
    }

    return {
      resignationDate: resigDateStr,
      diffDays,
      dDayLabel: label,
      badgeClass,
      subMessage,
      contract: myApprovedResignation
    };
  })();

  const [sendKakaoNotice, setSendKakaoNotice] = useState(false);
  const [kakaoSentSuccess, setKakaoSentSuccess] = useState(false);
  const [isSendingKakao, setIsSendingKakao] = useState(false);

  const storageInfo = getUserStorageUsage(currentUser?.UserID);

  const getSenderAvatarConfig = (senderEmail) => {
    if (!senderEmail) return null;
    const cleanEmail = senderEmail.toLowerCase().trim();
    const user = users.find(u => {
      const uEmail = (u.Email || '').toLowerCase().trim();
      const uAliases = (u.Aliases || '').toLowerCase().split(',').map(a => a.trim()).filter(Boolean);
      return uEmail === cleanEmail || uAliases.includes(cleanEmail);
    });
    return user ? user.AvatarConfig : null;
  };

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [mailViewLayout, setMailViewLayout] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_mail_view_layout_v1');
      if (saved === 'split' || saved === 'full') return saved;
    } catch (e) {}
    return 'full'; // Default: full-page list <-> detail reading mode with '❮ 목록으로'
  });

  useEffect(() => {
    try {
      localStorage.setItem('clou_mail_view_layout_v1', mailViewLayout);
    } catch (e) {}
  }, [mailViewLayout]);

  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [attachmentFilter, setAttachmentFilter] = useState(false);
  const [isSearchingDb, setIsSearchingDb] = useState(false);
  const [currentFolder, setCurrentFolder] = useState('inbox'); // 'inbox' | 'sent' | 'draft' | 'starred' | 'spam' | 'trash'
  const [unreadFilter, setUnreadFilter] = useState('all'); // 'all' | 'unread'
  const [selectedEmailIds, setSelectedEmailIds] = useState([]);
  const [lastSelectedEmailIndex, setLastSelectedEmailIndex] = useState(null);

  // Requirement 5: Custom Mailboxes & Trash Retention Period
  const [customFolders, setCustomFolders] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_custom_mailboxes_v1');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [trashRetentionDays, setTrashRetentionDays] = useState(() => {
    return Number(localStorage.getItem('clou_trash_retention_days')) || 7; // Default 7 days
  });

  const [trashEmailMap, setTrashEmailMap] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_trash_emails_v1');
      return saved ? JSON.parse(saved) : {}; // { [emailId]: deletedAtIsoString }
    } catch (e) {
      return {};
    }
  });

  const [customFolderMap, setCustomFolderMap] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_custom_folder_map_v1');
      return saved ? JSON.parse(saved) : {}; // { [emailId]: folderId }
    } catch (e) {
      return {};
    }
  });

  const saveCustomFolders = (folders) => {
    setCustomFolders(folders);
    localStorage.setItem('clou_custom_mailboxes_v1', JSON.stringify(folders));
  };

  const saveTrashRetentionDays = (days) => {
    setTrashRetentionDays(days);
    localStorage.setItem('clou_trash_retention_days', String(days));
  };

  const saveTrashEmailMap = (map) => {
    setTrashEmailMap(map);
    localStorage.setItem('clou_trash_emails_v1', JSON.stringify(map));
  };

  const saveCustomFolderMap = (map) => {
    setCustomFolderMap(map);
    localStorage.setItem('clou_custom_folder_map_v1', JSON.stringify(map));
  };

  const handleAddCustomFolder = () => {
    const folderName = prompt('추가할 메일함 이름을 입력하세요 (예: 거래처 서류, 중요 프로젝트):');
    if (!folderName || !folderName.trim()) return;
    const newFolder = {
      id: `folder-${Date.now()}`,
      name: folderName.trim()
    };
    saveCustomFolders([...customFolders, newFolder]);
    setCurrentFolder(newFolder.id);
  };

  const handleRenameCustomFolder = (folderId, oldName) => {
    const newName = prompt('수정할 메일함 이름을 입력하세요:', oldName);
    if (!newName || !newName.trim() || newName.trim() === oldName) return;
    saveCustomFolders(customFolders.map(f => f.id === folderId ? { ...f, name: newName.trim() } : f));
  };

  const handleDeleteCustomFolder = (folderId, folderName) => {
    if (confirm(`'${folderName}' 메일함을 삭제하시겠습니까?\n(해당 메일함에 보관되어 있던 메일들은 모두 '받은메일함'으로 원복됩니다)`)) {
      saveCustomFolders(customFolders.filter(f => f.id !== folderId));

      // Clear folder mappings for this custom folder
      const nextFolderMap = { ...customFolderMap };
      Object.keys(nextFolderMap).forEach(emailId => {
        if (nextFolderMap[emailId] === folderId) {
          delete nextFolderMap[emailId];
        }
      });
      saveCustomFolderMap(nextFolderMap);

      // Clear routing rules pointing to this folder
      const nextRoutingRules = { ...senderRoutingRules };
      Object.keys(nextRoutingRules).forEach(sender => {
        if (nextRoutingRules[sender] === folderId) {
          delete nextRoutingRules[sender];
        }
      });
      saveSenderRoutingRules(nextRoutingRules);

      if (currentFolder === folderId) setCurrentFolder('inbox');
    }
  };

  const handleMoveToTrash = (emailIds) => {
    const ids = Array.isArray(emailIds) ? emailIds : [emailIds];
    const nowIso = new Date().toISOString();
    const nextMap = { ...trashEmailMap };
    ids.forEach(id => { nextMap[id] = nowIso; });
    saveTrashEmailMap(nextMap);
    moveEmailsToTrash(ids);
    setSelectedEmailIds([]);
    if (selectedEmail && ids.includes(selectedEmail.EmailID)) setSelectedEmail(null);
  };

  const handleRestoreFromTrash = (emailIds) => {
    const ids = Array.isArray(emailIds) ? emailIds : [emailIds];
    const nextMap = { ...trashEmailMap };
    ids.forEach(id => { delete nextMap[id]; });
    saveTrashEmailMap(nextMap);
    restoreEmailsFromTrash(ids);
    setSelectedEmailIds([]);
  };

  const handlePermanentDelete = (emailIds) => {
    const ids = Array.isArray(emailIds) ? emailIds : [emailIds];
    deleteEmails(ids);
    const nextMap = { ...trashEmailMap };
    ids.forEach(id => { delete nextMap[id]; });
    saveTrashEmailMap(nextMap);
    setSelectedEmailIds([]);
    if (selectedEmail && ids.includes(selectedEmail.EmailID)) setSelectedEmail(null);
  };

  const handleEmptyTrash = () => {
    const trashMails = emails.filter(e => isEmailTrash(e) && (isMyInboxEmail(e) || isSentEmail(e)));
    const trashIds = trashMails.map(e => e.EmailID);
    if (trashIds.length === 0) {
      alert('휴지통이 비어있습니다.');
      return;
    }
    if (confirm(`휴지통의 메일 ${trashIds.length}개를 영구 삭제하시겠습니까? (복구 불가)`)) {
      handlePermanentDelete(trashIds);
    }
  };

  // Automatic Sender Routing Rules
  const [senderRoutingRules, setSenderRoutingRules] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_sender_routing_rules_v1');
      return saved ? JSON.parse(saved) : {}; // { [senderEmail]: folderId }
    } catch (e) {
      return {};
    }
  });

  const saveSenderRoutingRules = (rules) => {
    setSenderRoutingRules(rules);
    localStorage.setItem('clou_sender_routing_rules_v1', JSON.stringify(rules));
  };

  const [moveOptionModal, setMoveOptionModal] = useState({
    isOpen: false,
    emailIds: [],
    targetFolderId: '',
    targetFolderName: '',
    sender: '',
    sameSenderCount: 0
  });

  const promptMoveToCustomFolder = (emailIds, targetFolderId) => {
    const ids = Array.isArray(emailIds) ? emailIds : [emailIds];
    if (ids.length === 0 || !targetFolderId) return;

    const targetFolderObj = customFolders.find(f => f.id === targetFolderId);
    const targetFolderName = targetFolderId === 'inbox' ? '받은메일함' : (targetFolderObj ? targetFolderObj.name : '해당 메일함');

    // Never touch sent emails or trashed emails
    const validMails = emails.filter(e => ids.includes(e.EmailID) && !isEmailTrash(e) && !isSentEmail(e));
    if (validMails.length === 0) return;

    const primarySender = (validMails[0].Sender || '').toLowerCase();
    // Only non-trash, NON-SENT received inbox emails
    const sameSenderMails = emails.filter(e => !isEmailTrash(e) && !isSentEmail(e) && isMyInboxEmail(e) && (e.Sender || '').toLowerCase() === primarySender);

    if (sameSenderMails.length > 1) {
      setMoveOptionModal({
        isOpen: true,
        emailIds: ids,
        targetFolderId,
        targetFolderName,
        sender: validMails[0].Sender || primarySender,
        sameSenderCount: sameSenderMails.length
      });
    } else {
      executeMoveToCustomFolder(ids, targetFolderId, false);
    }
  };

  const executeMoveToCustomFolder = (emailIds, targetFolderId, moveAllBySender = false) => {
    const ids = Array.isArray(emailIds) ? emailIds : [emailIds];
    const validMails = emails.filter(e => ids.includes(e.EmailID) && !isEmailTrash(e) && !isSentEmail(e));
    const nextMap = { ...customFolderMap };
    const nextRules = { ...senderRoutingRules };

    if (moveAllBySender && validMails.length > 0) {
      const sender = (validMails[0].Sender || '').toLowerCase();
      // Only non-trash, NON-SENT received inbox emails
      const sameSenderMails = emails.filter(e => !isEmailTrash(e) && !isSentEmail(e) && isMyInboxEmail(e) && (e.Sender || '').toLowerCase() === sender);

      if (targetFolderId === 'inbox') {
        delete nextRules[sender];
        sameSenderMails.forEach(e => {
          delete nextMap[e.EmailID];
        });
      } else {
        nextRules[sender] = targetFolderId;
        sameSenderMails.forEach(e => {
          nextMap[e.EmailID] = targetFolderId;
        });
      }
      saveSenderRoutingRules(nextRules);
    } else {
      ids.forEach(id => {
        if (targetFolderId === 'inbox') {
          delete nextMap[id];
        } else {
          nextMap[id] = targetFolderId;
        }
      });
    }

    saveCustomFolderMap(nextMap);
    setSelectedEmailIds([]);
    setMoveOptionModal(prev => ({ ...prev, isOpen: false }));
  };

  // Auto-purge expired trash emails permanently from AWS DynamoDB & local state
  useEffect(() => {
    if (!emails || emails.length === 0) return;
    const now = Date.now();
    const expiredIds = [];
    const nextTrashMap = { ...trashEmailMap };
    let mapChanged = false;

    emails.forEach(email => {
      if (isEmailTrash(email) && (isMyInboxEmail(email) || isSentEmail(email))) {
        const deletedAt = trashEmailMap[email.EmailID] || email.DeletedAt || email.CreatedAt || email.Timestamp || email.DeliveredAt || email.Date;
        if (deletedAt) {
          const elapsedDays = (now - new Date(deletedAt).getTime()) / (1000 * 60 * 60 * 24);
          if (elapsedDays >= trashRetentionDays) {
            expiredIds.push(email.EmailID);
            if (nextTrashMap[email.EmailID]) {
              delete nextTrashMap[email.EmailID];
              mapChanged = true;
            }
          }
        }
      }
    });

    if (expiredIds.length > 0) {
      console.log(`[휴지통 보존기간 만료 자동 영구삭제] ${expiredIds.length}건 영구 삭제 실행 (${trashRetentionDays}일 경과)`);
      deleteEmails(expiredIds);
      if (mapChanged) {
        saveTrashEmailMap(nextTrashMap);
      }
    }
  }, [trashRetentionDays, emails, trashEmailMap]);

  // Reset scroll pagination on folder, filter, or search change
  useEffect(() => {
    setVisibleCount(25);
  }, [currentFolder, unreadFilter, searchTerm]);

  // Debounced Remote DynamoDB Search
  useEffect(() => {
    if (!searchTerm || !searchTerm.trim()) return;
    const timer = setTimeout(async () => {
      if (typeof fetchRemoteEmails === 'function') {
        setIsSearchingDb(true);
        try {
          await fetchRemoteEmails();
        } catch (err) {
          console.warn('PC remote search sync error:', err);
        } finally {
          setIsSearchingDb(false);
        }
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSelectFolder = (folderKey) => {
    if (isComposing && (composeSubject || composeBody || composeTo)) {
      if (!confirm('작성 중인 메일이 있습니다. 메일함 목록으로 이동하시겠습니까? (작성 내용은 임시보관함에 저장할 수 있습니다)')) {
        return;
      }
    }
    setIsComposing(false);
    setCurrentFolder(folderKey);
    setSelectedEmail(null);
    setSelectedEmailIds([]);
    setLastSelectedEmailIndex(null);
    setVisibleCount(25);
  };

  // Reset selected mail and checkboxes when currentFolder changes
  useEffect(() => {
    setSelectedEmail(null);
    setSelectedEmailIds([]);
    setLastSelectedEmailIndex(null);
  }, [currentFolder]);

  // Resizable Panel Widths (saved to localStorage for PC)
  const [folderWidth, setFolderWidth] = useState(() => {
    const saved = localStorage.getItem('clou_mail_folder_width');
    return saved ? Math.max(160, Math.min(360, parseInt(saved, 10))) : 220;
  });
  const [listWidth, setListWidth] = useState(() => {
    const saved = localStorage.getItem('clou_mail_list_width');
    return saved ? Math.max(260, Math.min(500, parseInt(saved, 10))) : 380;
  });
  const [isFolderCollapsed, setIsFolderCollapsed] = useState(false);

  // Dragging states
  const [isDraggingFolder, setIsDraggingFolder] = useState(false);
  const [isDraggingList, setIsDraggingList] = useState(false);
  const dragStartXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Drag Handlers for Folder Splitter
  const handleFolderMouseDown = (e) => {
    e.preventDefault();
    setIsDraggingFolder(true);
    dragStartXRef.current = e.clientX;
    startWidthRef.current = folderWidth;
  };

  // Drag Handlers for List Splitter
  const handleListMouseDown = (e) => {
    e.preventDefault();
    setIsDraggingList(true);
    dragStartXRef.current = e.clientX;
    startWidthRef.current = listWidth;
  };

  // Reset panels to default
  const handleResetPanelSizes = () => {
    setFolderWidth(220);
    setListWidth(380);
    setIsFolderCollapsed(false);
    localStorage.removeItem('clou_mail_folder_width');
    localStorage.removeItem('clou_mail_list_width');
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingFolder) {
        const delta = e.clientX - dragStartXRef.current;
        const newWidth = Math.max(160, Math.min(360, startWidthRef.current + delta));
        setFolderWidth(newWidth);
        localStorage.setItem('clou_mail_folder_width', String(newWidth));
      } else if (isDraggingList) {
        const delta = e.clientX - dragStartXRef.current;
        const newWidth = Math.max(260, Math.min(750, startWidthRef.current + delta));
        setListWidth(newWidth);
        localStorage.setItem('clou_mail_list_width', String(newWidth));
      }
    };

    const handleMouseUp = () => {
      if (isDraggingFolder) setIsDraggingFolder(false);
      if (isDraggingList) setIsDraggingList(false);
    };

    if (isDraggingFolder || isDraggingList) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingFolder, isDraggingList]);

  // Mobile Detail Modal State
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  // Read Receipt (수신 확인) Modal State - Requirement 2
  const [showReadReceiptModal, setShowReadReceiptModal] = useState(false);

  // User Aliases & Available Sender Options (Requirement 3)
  const userDetails = (users || []).find(u => 
    (u.UserID && currentUser?.UserID && u.UserID === currentUser.UserID) ||
    (u.Email && currentUser?.Email && u.Email.toLowerCase() === currentUser.Email.toLowerCase()) ||
    (u.Account && currentUser?.Account && u.Account.toLowerCase() === currentUser.Account.toLowerCase()) ||
    (u.Aliases && currentUser?.Email && u.Aliases.toLowerCase().includes(currentUser.Email.toLowerCase()))
  ) || currentUser;

  const rawAliasesStr = `${userDetails?.Aliases || ''},${currentUser?.Aliases || ''},${userDetails?.Email || ''},${currentUser?.Email || ''},${userDetails?.Account || ''},${currentUser?.Account || ''}`;
  const baseAliases = rawAliasesStr.split(/[,;\s]+/).map(a => a.trim().toLowerCase()).filter(Boolean);
  
  const currentEmailLower = (currentUser?.Email || userDetails?.Email || '').toLowerCase();
  const currentAccountLower = (currentUser?.Account || userDetails?.Account || '').toLowerCase();
  if (currentEmailLower.includes('chris') || currentAccountLower === 'chris') {
    baseAliases.push('ceo@clouflake.com', 'executive@clouflake.com', 'chris', 'ceo', 'executive');
  }
  if (currentEmailLower.includes('clou0315') || currentAccountLower === 'clou0315') {
    baseAliases.push('anderson@clouflake.com', 'clou0315', 'anderson');
  }
  if (currentEmailLower.includes('jykwon') || currentAccountLower === 'jykwon') {
    baseAliases.push('jykwon@clouflake.com', 'jykwon');
  }

  const userAliases = Array.from(new Set(baseAliases));
  const primaryAlias = userAliases.length > 0 ? (userAliases[0].includes('@') ? userAliases[0] : `${userAliases[0]}@clouflake.com`) : (currentUser?.Email || '');
  const defaultSenderEmail = userDetails?.DefaultSenderEmail || currentUser?.DefaultSenderEmail || primaryAlias || currentUser?.Email;
  const availableSenderEmails = Array.from(new Set([
    ...(defaultSenderEmail ? [defaultSenderEmail] : []),
    ...(primaryAlias ? [primaryAlias] : []),
    currentUser?.Email,
    ...userAliases.map(a => a.includes('@') ? a : `${a}@clouflake.com`)
  ].filter(Boolean)));

  // Compose state
  const [isComposing, setIsComposing] = useState(() => !!initialCompose?.isComposing);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isSendToSelf, setIsSendToSelf] = useState(false);
  const [isImportant, setIsImportant] = useState(false);
  const [showComposePreview, setShowComposePreview] = useState(false);
  const [composeFrom, setComposeFrom] = useState(defaultSenderEmail);
  const [composeTo, setComposeTo] = useState(() => initialCompose?.to || '');
  const [composeCc, setComposeCc] = useState(() => initialCompose?.cc || '');
  const [composeBcc, setComposeBcc] = useState(() => initialCompose?.bcc || '');
  const [showCcBcc, setShowCcBcc] = useState(() => !!(initialCompose?.cc || initialCompose?.bcc));
  const [composeSubject, setComposeSubject] = useState(() => initialCompose?.subject || '');
  const [composeBody, setComposeBody] = useState(() => initialCompose?.body || '');
  const [forwardQuotedEmail, setForwardQuotedEmail] = useState(null);
  const [includeQuotedEmail, setIncludeQuotedEmail] = useState(true);
  
  // Real Multiple File Attachments State & Drag-and-Drop
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef(null);

  const handleOpenCompose = (options = {}) => {
    setActiveDraftId(options.draftId || null);
    setForwardQuotedEmail(options.forwardQuotedEmail || null);
    setIncludeQuotedEmail(true);
    setComposeTo(options.to || (options.isSendToSelf ? (primaryAlias || currentUser?.Email || '') : ''));
    setComposeCc(options.cc || '');
    setComposeBcc(options.bcc || '');
    setShowCcBcc(Boolean(options.cc || options.bcc));
    setComposeSubject(options.subject || '');
    setComposeBody(options.body || '');
    setAttachedFiles(options.attachedFiles || []);
    setIsSendToSelf(Boolean(options.isSendToSelf));
    setIsImportant(Boolean(options.isImportant));
    setSelectedEmail(null);
    setIsComposing(true);
  };

  // Attachment Preview Modal State (Requirement 4)
  const [previewAttachment, setPreviewAttachment] = useState(null);

  // Smart Recipient Autocomplete State
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [showCcDropdown, setShowCcDropdown] = useState(false);
  const [showBccDropdown, setShowBccDropdown] = useState(false);

  // Mail Backup & Restore State
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [backupRestoreProgress, setBackupRestoreProgress] = useState(0);
  const [backupRestoreResult, setBackupRestoreResult] = useState(null);
  const [isExportingBackup, setIsExportingBackup] = useState(false);
  const zipFileInputRef = useRef(null);

  // Update composeFrom when primaryAlias changes
  useEffect(() => {
    if (primaryAlias) {
      setComposeFrom(primaryAlias);
    } else {
      setComposeFrom(currentUser.Email);
    }
  }, [primaryAlias, currentUser.Email]);

  // Autocomplete search helper
  const getSearchMatches = (inputVal) => {
    const lastToken = (inputVal || '').split(',').pop().trim().toLowerCase();
    if (!lastToken) return { employees: [], contacts: [] };
    const employees = (users || []).filter(u =>
      (u.Name || u.name || '').toLowerCase().includes(lastToken) ||
      (u.Email || u.email || '').toLowerCase().includes(lastToken) ||
      (u.Aliases || u.aliases || '').toLowerCase().includes(lastToken) ||
      (u.Department || u.department || '').toLowerCase().includes(lastToken) ||
      (u.JobTitle || u.jobTitle || u.Position || u.position || '').toLowerCase().includes(lastToken)
    ).slice(0, 5);
    const sentRecipientsList = Array.from(new Set((emails || []).map(e => e.Recipient).filter(r => r && r.includes('@'))));
    const contacts = [
      ...(customers || []).filter(c => 
        (c.Name || c.name || '').toLowerCase().includes(lastToken) || 
        (c.Email || c.email || '').toLowerCase().includes(lastToken) ||
        (c.Company || c.company || '').toLowerCase().includes(lastToken)
      ).map(c => ({ 
        Name: c.Name || c.name || '', 
        Email: c.Email || c.email || '', 
        Source: '주소록', 
        Subtitle: c.Company || c.company || '외부 고객' 
      })),
      ...sentRecipientsList.filter(r => 
        (r || '').toLowerCase().includes(lastToken) && 
        !(users || []).some(u => (u.Email || u.email || '').toLowerCase() === (r || '').toLowerCase()) && 
        !(customers || []).some(c => (c.Email || c.email || '').toLowerCase() === (r || '').toLowerCase())
      ).map(r => ({ Name: r.split('@')[0], Email: r, Source: '최근 발신', Subtitle: '최근 발신 이력' }))
    ].slice(0, 4);
    return { employees, contacts };
  };

  const matchingEmployees = getSearchMatches(composeTo).employees;
  const matchingContacts = getSearchMatches(composeTo).contacts;
  const matchingCc = getSearchMatches(composeCc);
  const matchingBcc = getSearchMatches(composeBcc);

  const handleRecipientInputKeyDown = (e, currentValue, setValue, matches, setDropdown) => {
    if (e.key === 'Enter') {
      // Prevent Korean/CJK IME composition duplicate trigger
      if (e.nativeEvent && e.nativeEvent.isComposing) return;
      e.preventDefault();
      e.stopPropagation();

      const allMatches = [...(matches?.employees || []), ...(matches?.contacts || [])];
      if (allMatches.length > 0) {
        const selected = allMatches[0];
        const tokens = currentValue.split(',').map(t => t.trim()).filter(Boolean);
        tokens.pop();
        tokens.push(selected.Email);
        setValue(tokens.join(', ') + ', ');
        setDropdown(false);
      } else {
        setDropdown(false);
      }
    } else if (e.key === 'Escape') {
      setDropdown(false);
    }
  };

  const isExternalSender = selectedEmail && !(selectedEmail.Sender || '').toLowerCase().endsWith('@clouflake.com');

  // Format file size utility
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Helper: Download Base64 Data URL as pure binary Blob
  const downloadBase64Blob = (dataUrl, filename) => {
    try {
      const parts = dataUrl.split(',');
      const mimeMatch = parts[0].match(/:(.*?);/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const b64Data = parts[1];
      const byteCharacters = atob(b64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Failed to convert base64 to Blob download:', err);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Real Attachment Downloader with S3 Presigned URL & Safe Binary Blob Engine
  const handleDownloadAttachment = async (filename) => {
    try {
      // Find if selectedEmail.Attachments has dataUrl or if attachedFiles has it
      const emailAtt = selectedEmail?.Attachments?.find(att => (typeof att === 'object' ? att.name : att.split('/').pop()) === filename);
      
      // If the attachment is stored in S3 (received email string key or object with s3Key)
      const cleanEmailId = selectedEmail?.EmailID ? selectedEmail.EmailID.replace(/-inbox.*/, '') : '';
      const targetS3Key = typeof emailAtt === 'string'
        ? (emailAtt.includes('/') ? emailAtt : `attachments/${cleanEmailId}/${emailAtt}`)
        : (emailAtt?.s3Key || emailAtt?.key || null);

      if (targetS3Key) {
        const res = await fetch(`https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/mail/attachment?key=${encodeURIComponent(targetS3Key)}`);
        const data = await res.json();
        
        // 1. Direct High-Speed S3 Binary Download via Pre-signed URL (100% Raw Binary Fidelity, No size limit)
        if (data.success && data.downloadUrl) {
          const link = document.createElement('a');
          link.href = data.downloadUrl;
          link.target = '_blank';
          link.rel = 'noopener,noreferrer';
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }

        // 2. Base64 DataUrl fallback converted to Pure Binary Blob
        if (data.success && data.dataUrl) {
          downloadBase64Blob(data.dataUrl, filename);
          return;
        }
      }

      const localDataUrl = (emailAtt && typeof emailAtt === 'object' && emailAtt.dataUrl) 
        ? emailAtt.dataUrl 
        : attachedFiles.find(f => f.name === filename)?.dataUrl;

      if (localDataUrl) {
        downloadBase64Blob(localDataUrl, filename);
        return;
      }

      // Automatic Fallback Lookup by clean Email ID
      if (cleanEmailId) {
        const fallbackKey = `attachments/${cleanEmailId}/${filename}`;
        if (fallbackKey !== targetS3Key) {
          try {
            const fbRes = await fetch(`https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/mail/attachment?key=${encodeURIComponent(fallbackKey)}`);
            const fbData = await fbRes.json();
            if (fbData.success && fbData.downloadUrl) {
              const link = document.createElement('a');
              link.href = fbData.downloadUrl;
              link.target = '_blank';
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              return;
            } else if (fbData.success && fbData.dataUrl) {
              downloadBase64Blob(fbData.dataUrl, filename);
              return;
            }
          } catch (fbErr) {
            console.warn('Fallback download lookup failed:', fbErr);
          }
        }
      }

      alert(`첨부파일 '${filename}'을(를) 다운로드할 수 없습니다. 원본 파일이 서버 저장소에 존재하지 않습니다.`);
    } catch (err) {
      alert(`파일 다운로드 중 에러가 발생했습니다: ${err.message}`);
    }
  };

  // Drag and Drop handlers for file attachments
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingFile) setIsDraggingFile(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingFile(false);
  };

  // Helper to upload files directly to S3 via pre-signed URL
  const uploadAndAttachFiles = async (files) => {
    if (!files || !files.length) return;

    for (const file of files) {
      const placeholder = {
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
        s3Key: null,
        isUploading: true,
        error: null
      };

      setAttachedFiles(prev => {
        if (prev.some(f => f.name === file.name)) return prev;
        return [...prev, placeholder];
      });

      try {
        const presignRes = await fetch('https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/mail/presign', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type || 'application/octet-stream'
          })
        });
        const presignData = await presignRes.json();
        
        if (!presignData.success || !presignData.uploadUrl) {
          throw new Error(presignData.error || 'Failed to get upload URL');
        }

        const { uploadUrl, s3Key } = presignData;

        // Direct binary upload to S3 bucket via PUT
        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          },
          body: file
        });

        if (!uploadRes.ok) {
          throw new Error(`S3 upload failed: ${uploadRes.statusText}`);
        }

        setAttachedFiles(prev => prev.map(f => f.name === file.name ? {
          ...f,
          s3Key,
          isUploading: false
        } : f));
        console.log(`Directly uploaded composition file ${file.name} to S3 Key: ${s3Key}`);

      } catch (err) {
        console.error(`S3 Direct Upload failure for ${file.name}:`, err);
        setAttachedFiles(prev => prev.map(f => f.name === file.name ? {
          ...f,
          isUploading: false,
          error: '업로드 실패'
        } : f));
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    uploadAndAttachFiles(files);
  };

  // Handle file selection from local filesystem
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    uploadAndAttachFiles(files);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attached file
  const handleRemoveAttachedFile = (indexToRemove) => {
    setAttachedFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  // Quick Address Book registration from email header
  const handleAddToAddressBook = () => {
    if (!selectedEmail) return;
    const senderEmail = selectedEmail.Sender.toLowerCase();
    
    const exists = customers.some(c => c.Email.toLowerCase() === senderEmail);
    if (exists) {
      alert('이미 주소록에 등록된 고객 이메일입니다.');
      return;
    }

    const emailNamePart = selectedEmail.Sender.split('@')[0];
    const defaultName = emailNamePart.charAt(0).toUpperCase() + emailNamePart.slice(1);
    const customName = prompt('주소록에 등록할 고객 이름을 입력해 주세요:', defaultName);
    if (customName === null) return;
    
    const finalName = customName.trim() || defaultName;
    addCustomer({
      Name: finalName,
      Email: selectedEmail.Sender,
      Phone: '',
      Company: '',
      Memo: '수신 이메일 상세 화면에서 빠른 등록됨'
    });
    alert(`고객 '${finalName}' (${selectedEmail.Sender}) 주소록 등록이 완료되었습니다.`);
  };

  // Watch initialCompose prop changes
  useEffect(() => {
    if (initialCompose && initialCompose.isComposing) {
      setActiveDraftId(null);
      setComposeTo(initialCompose.to || '');
      setComposeCc(initialCompose.cc || '');
      setComposeBcc(initialCompose.bcc || '');
      setShowCcBcc(!!(initialCompose.cc || initialCompose.bcc));
      setComposeSubject(initialCompose.subject || '');
      setComposeBody(initialCompose.body || '');
      setAttachedFiles(initialCompose.attachments || []);
      setIsComposing(true);
    }
  }, [initialCompose]);

  // Listen to Compose event from sidebar and other tabs
  useEffect(() => {
    const handleCompose = (e) => {
      const detail = e.detail || {};
      setActiveDraftId(null);
      setComposeTo(detail.to || '');
      setComposeCc(detail.cc || '');
      setComposeBcc(detail.bcc || '');
      if (detail.cc || detail.bcc) {
        setShowCcBcc(true);
      } else {
        setShowCcBcc(false);
      }
      setComposeSubject(detail.subject || '');
      setComposeBody(detail.body || '');
      setAttachedFiles(detail.attachments || []);
      setIsComposing(true);
    };
    window.addEventListener('trigger-compose', handleCompose);
    return () => {
      window.removeEventListener('trigger-compose', handleCompose);
    };
  }, []);

  // Korean Date Formatter for email quotes (Matches standard portal webmail format)
  const formatQuoteDate = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return String(timestamp);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
    const weekDay = weekDays[d.getDay()];
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} (${weekDay}) ${hours}:${minutes}:${seconds} (GMT+09:00)`;
  };

  // Helper to generate rich HTML quote block (Preserving 100% of original HTML, tables, images, and styles)
  const formatQuoteBodyHtml = (email) => {
    if (!email) return '';
    const dateStr = formatQuoteDate(email.Timestamp || email.DeliveredAt || email.Date || email.CreatedAt || Date.now());
    const senderDisplay = email.SenderName 
      ? `"${email.SenderName}" &lt;${email.Sender}&gt;` 
      : (email.Sender?.includes('<') ? email.Sender.replace(/</g, '&lt;').replace(/>/g, '&gt;') : `"${email.Sender}" &lt;${email.Sender}&gt;`);
    const recipientDisplay = email.Recipient?.includes('<') ? email.Recipient.replace(/</g, '&lt;').replace(/>/g, '&gt;') : `"${email.Recipient}" &lt;${email.Recipient}&gt;`;
    const ccDisplay = email.Cc ? (email.Cc.includes('<') ? email.Cc.replace(/</g, '&lt;').replace(/>/g, '&gt;') : email.Cc) : '';
    const subjectDisplay = email.Subject || '';
    
    // PRESERVE ORIGINAL HTML (TABLES, FORMATTING, STYLES, IMAGES)
    let originalHtml = '';
    if (email.BodyHTML && email.BodyHTML.trim().startsWith('<')) {
      originalHtml = email.BodyHTML;
    } else if (email.BodyHTML) {
      originalHtml = convertTextToHtml(email.BodyHTML);
    } else if (email.BodyText) {
      originalHtml = convertTextToHtml(email.BodyText);
    } else {
      originalHtml = '<p>(본문 내용 없음)</p>';
    }

    return `<p><br></p><p><br></p>` +
      `<div class="clou-mail-quote" style="margin-top: 24px; padding-top: 12px; font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', sans-serif; font-size: 13px; color: #334155; line-height: 1.6;">` +
      `<p style="margin: 0 0 8px 0; font-weight: 700; color: #0f172a; font-size: 13px;">-----Original Message-----</p>` +
      `<p style="margin: 2px 0;"><strong>From:</strong> ${senderDisplay}</p>` +
      `<p style="margin: 2px 0;"><strong>To:</strong> ${recipientDisplay}</p>` +
      (ccDisplay ? `<p style="margin: 2px 0;"><strong>Cc:</strong> ${ccDisplay}</p>` : `<p style="margin: 2px 0;"><strong>Cc:</strong> </p>`) +
      `<p style="margin: 2px 0;"><strong>Sent:</strong> ${dateStr}</p>` +
      `<p style="margin: 2px 0 14px 0;"><strong>Subject:</strong> ${subjectDisplay}</p>` +
      `<div class="clou-quote-content" style="margin-top: 14px; font-size: 14px; color: #0f172a;">${originalHtml}</div>` +
      `</div>`;
  };

  // Helper to convert plain text newlines into HTML <br> while preserving existing HTML
  const convertTextToHtml = (text) => {
    if (!text) return '';
    if (text.includes('<p>') || text.includes('<div>') || text.includes('<blockquote') || text.includes('<br')) {
      return text;
    }
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped.replace(/\r\n|\r|\n/g, '<br />');
  };

  // Reply to selected email
  const handleReply = (email) => {
    if (!email) return;
    const targetEmail = email.Sender;
    setForwardQuotedEmail(email);
    setIncludeQuotedEmail(true);
    setComposeTo(targetEmail);
    setComposeCc('');
    setComposeBcc('');
    setShowCcBcc(false);
    setComposeSubject(email.Subject.startsWith('Re:') ? email.Subject : `Re: ${email.Subject}`);
    setComposeBody(formatQuoteBodyHtml(email));
    setAttachedFiles([]);
    setActiveDraftId(null);
    setIsSendToSelf(false);
    setSelectedEmail(null);
    setIsComposing(true);
  };

  // Reply All
  const handleReplyAll = (email) => {
    if (!email) return;
    const targetEmail = email.Sender;
    // Collect all recipients excluding self
    const allCc = [email.Recipient, ...(email.Cc ? email.Cc.split(',') : [])]
      .map(s => s.trim())
      .filter(s => s && s.toLowerCase() !== currentUser?.Email?.toLowerCase() && s.toLowerCase() !== targetEmail.toLowerCase());
    
    setForwardQuotedEmail(email);
    setIncludeQuotedEmail(true);
    setComposeTo(targetEmail);
    setComposeCc(allCc.join(', '));
    setComposeBcc('');
    setShowCcBcc(allCc.length > 0);
    setComposeSubject(email.Subject.startsWith('Re:') ? email.Subject : `Re: ${email.Subject}`);
    setComposeBody(formatQuoteBodyHtml(email));
    setAttachedFiles([]);
    setActiveDraftId(null);
    setIsSendToSelf(false);
    setSelectedEmail(null);
    setIsComposing(true);
  };

  // Forward
  const handleForward = (email) => {
    if (!email) return;
    setForwardQuotedEmail(email);
    setIncludeQuotedEmail(true);
    setComposeTo('');
    setComposeCc('');
    setComposeBcc('');
    setShowCcBcc(false);
    setComposeSubject(email.Subject.startsWith('FW:') || email.Subject.startsWith('Fwd:') ? email.Subject : `FW: ${email.Subject}`);
    setComposeBody(formatQuoteBodyHtml(email));
    setAttachedFiles(email.Attachments ? email.Attachments.map(att => {
      if (typeof att === 'string') {
        return { name: att.split('/').pop(), size: 256000, type: 'application/octet-stream', s3Key: att };
      } else {
        return {
          name: att.name || 'attachment',
          size: att.size || 0,
          type: att.type || '',
          s3Key: att.s3Key || att.key || null
        };
      }
    }) : []);
    setActiveDraftId(null);
    setIsSendToSelf(false);
    setSelectedEmail(null);
    setIsComposing(true);
  };

  // Save current mail as draft
  const handleSaveDraft = () => {
    if (!composeSubject && !composeBody && !composeTo) {
      alert('임시 저장할 내용을 입력해 주세요.');
      return;
    }
    const saved = saveDraft({
      DraftID: activeDraftId,
      Recipient: composeTo,
      Subject: composeSubject || '(제목 없음)',
      BodyText: composeBody,
      Attachments: attachedFiles
    });
    setActiveDraftId(saved.DraftID);
    alert('임시보관함에 저장되었습니다.');
  };

  // Open existing draft into compose pane
  const handleOpenDraft = (draft) => {
    setActiveDraftId(draft.DraftID);
    setComposeTo(draft.Recipient || '');
    setComposeSubject(draft.Subject || '');
    setComposeBody(draft.BodyHTML || draft.BodyText || '');
    setAttachedFiles(draft.Attachments ? draft.Attachments.map(att => {
      if (typeof att === 'string') {
        return { name: att.includes('/') ? att.split('/').pop() : att, size: 256000, type: 'application/octet-stream', s3Key: att.includes('/') ? att : null };
      } else {
        return {
          name: att.name || 'attachment',
          size: att.size || 0,
          type: att.type || '',
          s3Key: att.s3Key || att.key || null
        };
      }
    }) : []);
    setSelectedEmail(null);
    setIsComposing(true);
  };

  // Spam helper functions (Requirement 3)
  const isSpamEmail = (email) => {
    if (!email) return false;
    if (email.IsSpam || email.Folder === 'spam') return true;
    const sender = (email.Sender || '').toLowerCase();
    return blockedSenders.some(b => b && typeof b === 'string' && b.trim() !== '' && sender.includes(b.toLowerCase().trim()));
  };

  const getGroupMailInfo = (email) => {
    if (!email || !groupMails || !Array.isArray(groupMails)) return null;
    const rec = (email.Recipient || '').toLowerCase();
    return groupMails.find(g => rec.includes((g.GroupEmail || '').toLowerCase()));
  };

  const isSentEmail = (email) => {
    if (!email) return false;
    // An inbox copy should never appear in sent folder
    if (email.EmailID?.includes('-inbox')) return false;
    
    const sender = (email.Sender || '').toLowerCase();
    const isMeSender = userAliases.some(alias => {
      const a = alias.toLowerCase().trim();
      if (!a) return false;
      if (a.includes('@')) return sender.includes(a);
      return sender.includes(`${a}@`) || sender.includes(`<${a}@`) || sender === a;
    });

    return isMeSender && email.Folder !== 'trash' && email.Folder !== 'spam';
  };

  const isMyInboxEmail = (email) => {
    if (!email) return false;

    // Sent emails sent by me (unless it's an inbox copy) should never be in inbox
    if (isSentEmail(email)) return false;

    const rec = (email.Recipient || '').toLowerCase();
    const cc = (email.Cc || '').toLowerCase();
    const bcc = (email.Bcc || '').toLowerCase();
    const owner = (email.OwnerEmail || '').toLowerCase();

    // 1. If this email was addressed to me (e.g. direct recipient, CC, BCC, OwnerEmail, self-mail, alias, broadcast), it belongs in inbox
    const isMeRecipient = userAliases.some(a => {
      const cleanA = a.toLowerCase().trim();
      if (!cleanA) return false;
      return rec.includes(cleanA) || cc.includes(cleanA) || bcc.includes(cleanA) || owner.includes(cleanA);
    }) || rec.includes('all@clouflake.com') || cc.includes('all@clouflake.com');

    if (isMeRecipient) return true;

    // 2. Representative Forwarding Group email match
    if (Array.isArray(groupMails)) {
      const isAssigned = groupMails.some(group => {
        const groupAddr = (group.GroupEmail || '').toLowerCase();
        if (!rec.includes(groupAddr) && !cc.includes(groupAddr)) return false;
        const assignees = (group.Assignees || []).map(a => a.toLowerCase().trim());
        return userAliases.some(a => assignees.includes(a));
      });
      if (isAssigned) return true;
    }
    
    return false;
  };

  const isEmailTrash = (email) => {
    if (!email) return false;
    return Boolean(trashEmailMap[email.EmailID]) || email.Folder === 'trash' || email.IsDeleted === true;
  };

  const getEffectiveCustomFolder = (email) => {
    if (!email) return null;
    if (isEmailTrash(email)) return null;
    // Sent emails never belong to a custom folder via sender routing
    if (isSentEmail(email) && !customFolderMap[email.EmailID]) return null;
    const sender = (email.Sender || '').toLowerCase();
    const autoRoutedFolder = senderRoutingRules[sender];
    return customFolderMap[email.EmailID] || autoRoutedFolder || null;
  };

  // Inbox emails: addressed to me, not spam, NOT in trash, and NOT moved to a custom folder
  const totalInboxEmails = emails.filter(e => {
    if (isEmailTrash(e)) return false;
    if (isSpamEmail(e)) return false;
    if (isSentEmail(e)) return false;
    if (getEffectiveCustomFolder(e)) return false;
    return isMyInboxEmail(e);
  });
  const unreadInboxCount = totalInboxEmails.filter(e => !e.IsRead).length;

  const totalSentEmails = emails.filter(e => isSentEmail(e) && !isEmailTrash(e));
  const totalSpamEmails = emails.filter(e => !isEmailTrash(e) && !isSentEmail(e) && isSpamEmail(e));

  const userStarredCount = emails.filter(email => {
    if (isEmailTrash(email)) return false;
    const isStarred = starredEmailIds.includes(email.EmailID);
    if (!isStarred) return false;
    const spam = isSpamEmail(email);
    const isInbox = isMyInboxEmail(email) && !spam && !getEffectiveCustomFolder(email);
    const isSent = isSentEmail(email);
    const isSpam = !isSent && spam;
    return isInbox || isSent || isSpam;
  }).length;

  const emailsWithAttachmentsCount = emails.filter(e => !isEmailTrash(e) && !isSentEmail(e) && !getEffectiveCustomFolder(e) && isMyInboxEmail(e) && !isSpamEmail(e) && Array.isArray(e.Attachments) && e.Attachments.length > 0).length;
  const selfEmailsCount = emails.filter(e => !isEmailTrash(e) && isSentEmail(e) && isMyInboxEmail(e)).length;

  // Filter emails based on folder, spam status, unread filter, and search term with strict privacy isolation & trash retention
  const filteredEmails = emails.filter(email => {
    const spam = isSpamEmail(email);
    const isTrash = isEmailTrash(email);
    const deletedAt = trashEmailMap[email.EmailID] || email.DeletedAt || email.CreatedAt || email.Timestamp || email.DeliveredAt || email.Date;

    // Auto-clean expired trash (Retention check)
    if (isTrash && deletedAt) {
      const deletedTime = new Date(deletedAt).getTime();
      const elapsedDays = (Date.now() - deletedTime) / (1000 * 60 * 60 * 24);
      if (elapsedDays >= trashRetentionDays) {
        return false; // Expired from Trash
      }
    }

    if (currentFolder === 'trash') {
      return isTrash && (isMyInboxEmail(email) || isSentEmail(email));
    }

    // Exclude trashed emails from non-trash folders
    if (isTrash) return false;

    // Sender Routing Rule Check
    const sender = (email.Sender || '').toLowerCase();
    const autoRoutedFolder = senderRoutingRules[sender];
    const effectiveFolder = customFolderMap[email.EmailID] || autoRoutedFolder;

    // Custom Mailbox filtering
    if (customFolders.some(f => f.id === currentFolder)) {
      return !isSentEmail(email) && effectiveFolder === currentFolder;
    }

    let folderMatch = false;

    if (currentFolder === 'inbox') {
      folderMatch = isMyInboxEmail(email) && !spam && !effectiveFolder;
      if (folderMatch && unreadFilter === 'unread') {
        folderMatch = (!email.IsRead || email.EmailID === selectedEmail?.EmailID);
      }
    } else if (currentFolder === 'sent') {
      folderMatch = isSentEmail(email);
    } else if (currentFolder === 'starred') {
      folderMatch = (isMyInboxEmail(email) || isSentEmail(email)) && starredEmailIds.includes(email.EmailID);
    } else if (currentFolder === 'spam') {
      folderMatch = !isSentEmail(email) && spam;
    } else if (currentFolder === 'all') {
      folderMatch = (isMyInboxEmail(email) || isSentEmail(email));
    } else {
      folderMatch = isMyInboxEmail(email) || isSentEmail(email);
    }
    
    if (attachmentFilter) {
      const hasAtt = Array.isArray(email.Attachments) && email.Attachments.length > 0;
      if (!hasAtt) return false;
    }

    const term = searchTerm.toLowerCase().trim();
    const matchesSearch = !term || 
                          (email.Subject || '').toLowerCase().includes(term) ||
                          (email.Sender || '').toLowerCase().includes(term) ||
                          (email.Recipient || '').toLowerCase().includes(term) ||
                          (email.BodyText || '').toLowerCase().includes(term);
    return folderMatch && matchesSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredEmails.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [currentFolder, unreadFilter, attachmentFilter, searchTerm, pageSize]);

  const displayedEmails = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredEmails.slice(start, start + pageSize);
  }, [filteredEmails, currentPage, pageSize]);

  const currentEmailIndex = useMemo(() => {
    if (!selectedEmail) return -1;
    return filteredEmails.findIndex(e => e.EmailID === selectedEmail.EmailID);
  }, [selectedEmail, filteredEmails]);

  const handlePrevEmail = () => {
    if (currentEmailIndex > 0) {
      handleSelectEmail(filteredEmails[currentEmailIndex - 1]);
    }
  };

  const handleNextEmail = () => {
    if (currentEmailIndex >= 0 && currentEmailIndex < filteredEmails.length - 1) {
      handleSelectEmail(filteredEmails[currentEmailIndex + 1]);
    }
  };

  const handleScrollEmailList = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      if (visibleCount < filteredEmails.length) {
        setVisibleCount(prev => Math.min(prev + 25, filteredEmails.length));
      }
    }
  };

  // Shift + Click Range Checkbox Selection Handler (Requirement 2)
  const handleCheckboxClick = (e, emailId, index) => {
    e.stopPropagation();
    if (e.shiftKey && lastSelectedEmailIndex !== null && lastSelectedEmailIndex !== index) {
      const start = Math.min(lastSelectedEmailIndex, index);
      const end = Math.max(lastSelectedEmailIndex, index);
      const rangeIds = filteredEmails.slice(start, end + 1).map(em => em.EmailID);
      setSelectedEmailIds(prev => {
        const newSet = new Set(prev);
        rangeIds.forEach(id => newSet.add(id));
        return Array.from(newSet);
      });
    } else {
      setSelectedEmailIds(prev =>
        prev.includes(emailId) ? prev.filter(id => id !== emailId) : [...prev, emailId]
      );
      setLastSelectedEmailIndex(index);
    }
  };

  const handleSelectAllToggle = () => {
    if (selectedEmailIds.length === filteredEmails.length && filteredEmails.length > 0) {
      setSelectedEmailIds([]);
      setLastSelectedEmailIndex(null);
    } else {
      setSelectedEmailIds(filteredEmails.map(e => e.EmailID));
    }
  };

  // Batch action handlers (Requirement 2 & 3)
  const handleBatchDelete = () => {
    if (selectedEmailIds.length === 0) return;
    if (currentFolder === 'trash') {
      if (confirm(`선택한 ${selectedEmailIds.length}개의 메일을 영구 삭제하시겠습니까? (복구 불가)`)) {
        handlePermanentDelete(selectedEmailIds);
      }
    } else {
      if (confirm(`선택한 ${selectedEmailIds.length}개의 메일을 휴지통으로 이동하시겠습니까?`)) {
        handleMoveToTrash(selectedEmailIds);
      }
    }
  };

  const handleBatchMarkRead = (isRead) => {
    if (selectedEmailIds.length === 0) return;
    markEmailsRead(selectedEmailIds, isRead);
    if (selectedEmail && selectedEmailIds.includes(selectedEmail.EmailID)) {
      setSelectedEmail(prev => prev ? { ...prev, IsRead: isRead } : null);
    }
    setSelectedEmailIds([]);
  };

  const handleBatchSpam = () => {
    if (selectedEmailIds.length === 0) return;
    if (confirm(`선택한 ${selectedEmailIds.length}개의 메일을 스팸으로 신고하고 해당 발신자를 차단하시겠습니까?`)) {
      reportSpamEmails(selectedEmailIds, true);
      if (selectedEmail && selectedEmailIds.includes(selectedEmail.EmailID)) {
        setSelectedEmail(null);
      }
      setSelectedEmailIds([]);
      setLastSelectedEmailIndex(null);
      alert('스팸 신고 및 발신자 차단이 완료되어 스팸메일함으로 이동되었습니다.');
    }
  };

  const handleSingleSpam = (email) => {
    if (!email) return;
    if (confirm(`[${email.Sender}] 발신자를 차단하고 스팸메일함으로 이동하시겠습니까?`)) {
      reportSpamEmails([email.EmailID], true);
      setSelectedEmail(null);
      alert(`'${email.Sender}' 발신자가 차단 목록에 추가되었으며 메일이 스팸함으로 이동되었습니다.`);
    }
  };

  const handleSingleRestoreSpam = (email) => {
    if (!email) return;
    restoreSpamEmails([email.EmailID]);
    unblockSender(email.Sender);
    alert(`스팸 차단이 해제되었으며 받은메일함으로 복원되었습니다.`);
  };

  // Send Email with full file attachments, CC and BCC
  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (isSending) return;

    // Block send if any attachment is still uploading to S3
    const uploadingFiles = attachedFiles.filter(f => f.isUploading);
    if (uploadingFiles.length > 0) {
      alert(`첨부파일(${uploadingFiles.map(f => f.name).join(', ')})이 아직 업로드 중입니다.\n모든 파일 업로드가 완료된 후 다시 발송해 주세요.`);
      return;
    }

    // Check if any attachment failed to upload
    const failedFiles = attachedFiles.filter(f => f.error);
    if (failedFiles.length > 0) {
      if (!confirm(`일부 첨부파일(${failedFiles.map(f => f.name).join(', ')})의 업로드가 실패했습니다.\n해당 파일을 제외하고 메일을 발송하시겠습니까?`)) {
        return;
      }
    }

    console.log('[handleSend Debug]', { composeTo, composeSubject, composeBody, sendKakaoNotice });
    if (!composeTo || !composeSubject || !composeBody) {
      console.warn('[handleSend Validation Failed]', { composeTo, composeSubject, composeBody });
      alert('받는 사람, 제목, 본문 내용을 모두 입력해 주세요.');
      return;
    }
    
    setIsSending(true);
    try {
      const attachmentsToPass = attachedFiles.map(f => ({
        name: f.name,
        size: f.size,
        type: f.type,
        s3Key: f.s3Key,
        dataUrl: f.dataUrl
      }));
      
      const htmlBodyToSend = composeBody || '';
      const textBodyToSend = stripHtmlTags(composeBody || '');
      const finalSubject = isImportant && !composeSubject.startsWith('[중요]') ? `[중요] ${composeSubject}` : composeSubject;

      await sendEmail(composeTo, finalSubject, textBodyToSend, htmlBodyToSend, attachmentsToPass, composeFrom, composeCc, composeBcc);
      
      // Dispatch KakaoTalk notification to recipient(s) if checked
      if (sendKakaoNotice && typeof sendKakaoBizMessage === 'function') {
        const parseTokens = (str) => {
          if (!str) return [];
          return str.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
        };

        const rawDestinations = [
          ...parseTokens(composeTo),
          ...parseTokens(composeCc),
          ...parseTokens(composeBcc)
        ];

        const matchedRecipients = [];

        for (const raw of rawDestinations) {
          const rawClean = raw.toLowerCase();
          const angleMatch = raw.match(/<([^>]+)>/);
          const emailPart = (angleMatch ? angleMatch[1] : (raw.includes('@') ? raw : '')).trim().toLowerCase();
          const namePart = (angleMatch ? raw.replace(/<[^>]+>/, '') : (!raw.includes('@') ? raw : '')).trim().toLowerCase();
          const idPrefix = emailPart ? emailPart.split('@')[0] : rawClean;
          const phoneOnly = raw.replace(/[^0-9]/g, '');

          // 0. Check Group Mails (e.g. all@clouflake.com, dev@clouflake.com)
          const isAllGroup = emailPart === 'all@clouflake.com' || rawClean.includes('all@clouflake.com') || rawClean === 'all' || rawClean === '전체';
          if (isAllGroup) {
            (users || []).filter(u => u.EmploymentStatus !== '퇴사' && u.Phone).forEach(u => {
              matchedRecipients.push({
                Name: u.Name,
                Email: u.Email,
                Phone: u.Phone
              });
            });
            continue;
          }

          const matchedGroup = (groupMails || []).find(g => {
            const gEmail = (g.GroupEmail || g.email || '').toLowerCase().trim();
            const gName = (g.GroupName || g.name || '').toLowerCase().trim();
            return (emailPart && gEmail === emailPart) || (namePart && gName === namePart) || rawClean === gEmail || rawClean === gName;
          });

          if (matchedGroup) {
            const memberIdsOrEmails = matchedGroup.Members || matchedGroup.members || [];
            (users || []).filter(u => {
              return memberIdsOrEmails.includes(u.UserID) || 
                     memberIdsOrEmails.includes(u.Email) || 
                     memberIdsOrEmails.some(m => m.toLowerCase() === (u.Email || '').toLowerCase());
            }).forEach(u => {
              if (u.Phone && u.EmploymentStatus !== '퇴사') {
                matchedRecipients.push({
                  Name: u.Name,
                  Email: u.Email,
                  Phone: u.Phone
                });
              }
            });
            continue;
          }

          // 1. Search in Internal Directory (users)
          let foundUser = (users || []).find(u => {
            const uEmail = (u.Email || '').toLowerCase().trim();
            const uName = (u.Name || '').toLowerCase().trim();
            const uAliases = (u.Aliases || '').toLowerCase().split(',').map(a => a.trim()).filter(Boolean);
            const uPhoneOnly = (u.Phone || '').replace(/[^0-9]/g, '');

            return (
              (emailPart && (uEmail === emailPart || uAliases.includes(emailPart))) ||
              (namePart && (uName === namePart || uName.includes(namePart))) ||
              (idPrefix && (uEmail.split('@')[0] === idPrefix || uAliases.some(a => a.split('@')[0] === idPrefix))) ||
              (rawClean && (uEmail === rawClean || uName === rawClean)) ||
              (phoneOnly.length >= 10 && uPhoneOnly === phoneOnly)
            );
          });

          if (foundUser) {
            matchedRecipients.push({
              Name: foundUser.Name,
              Email: foundUser.Email,
              Phone: foundUser.Phone
            });
            continue;
          }

          // 2. Search in Address Book External Contacts (customers)
          let foundCustomer = (customers || []).find(c => {
            const cEmail = (c.Email || '').toLowerCase().trim();
            const cName = (c.Name || '').toLowerCase().trim();
            const cPhoneOnly = (c.Phone || '').replace(/[^0-9]/g, '');

            return (
              (emailPart && cEmail === emailPart) ||
              (namePart && (cName === namePart || cName.includes(namePart))) ||
              (rawClean && (cEmail === rawClean || cName === rawClean)) ||
              (phoneOnly.length >= 10 && cPhoneOnly === phoneOnly)
            );
          });

          if (foundCustomer) {
            matchedRecipients.push({
              Name: foundCustomer.Name,
              Email: foundCustomer.Email,
              Phone: foundCustomer.Phone
            });
          } else {
            // Fallback: If sent to clou0315 or unknown internal, dispatch with email so backend resolves
            matchedRecipients.push({
              Name: namePart || idPrefix || '수신자',
              Email: emailPart || (idPrefix.includes('@') ? idPrefix : `${idPrefix}@clouflake.com`),
              Phone: phoneOnly.length >= 10 ? phoneOnly : ''
            });
          }
        }

        // Deduplicate by Email / Phone
        const uniqueRecipients = Array.from(new Map(matchedRecipients.map(r => [r.Email || r.Phone, r])).values());

        const plainBody = stripHtmlTags(composeBody);
        const formattedMail = typeof formatKakaoTemplate === 'function'
          ? formatKakaoTemplate('MAIL_SUMMARY', {
              '발신자': `${currentUser?.Name || '동료'} (${composeFrom || currentUser?.Email || 'clouflake'})`,
              '메일제목': composeSubject || '(제목 없음)',
              '수신일시': formatMailDateTime(new Date()),
              '요약내용': plainBody ? (plainBody.length > 200 ? plainBody.slice(0, 200) + '...' : plainBody) : '새로운 업무 메일이 도착했습니다.'
            })
          : null;

        const mailTitle = formattedMail?.title || `[클라우플레이크 메일 도착 알림]`;
        const mailBody = formattedMail?.messageText || `[클라우플레이크 메일 도착 알림]\n\n발신자: ${currentUser?.Name || '동료'}\n메일제목: ${composeSubject}\n수신일시: ${formatMailDateTime(new Date())}\n\n새로운 업무 메일이 도착했습니다.\n\n* 주식회사 클로우플레이크 AX 플랫폼`;
        const mailBtnText = formattedMail?.buttonText || '메일 바로보기';
        const mailBtnUrl = formattedMail?.buttonUrl || `${window.location.origin}/#/webmail`;

        for (const recipient of uniqueRecipients) {
          try {
            await sendKakaoBizMessage({
              recipientPhone: recipient.Phone,
              recipientName: recipient.Name,
              recipientEmail: recipient.Email,
              title: mailTitle,
              messageText: mailBody,
              buttonText: mailBtnText,
              buttonUrl: mailBtnUrl,
              messageType: 'MAIL',
              apiKey: kakaoBizConfig?.apiKey,
              apiSecret: kakaoBizConfig?.apiSecret,
              senderKey: kakaoBizConfig?.senderKey,
              senderPhone: kakaoBizConfig?.senderPhone,
              provider: kakaoBizConfig?.provider || 'solapi'
            });
          } catch (kErr) {
            console.warn('[Kakao Mail Dispatch Warning]', kErr);
          }
        }
      }

      // If was draft, delete draft
      if (activeDraftId) {
        deleteDraft(activeDraftId);
      }

      setComposeTo('');
      setComposeCc('');
      setComposeBcc('');
      setShowCcBcc(false);
      setComposeSubject('');
      setComposeBody('');
      setAttachedFiles([]);
      setActiveDraftId(null);
      setSendKakaoNotice(false);
      setIsSendToSelf(false);
      setIsImportant(false);
      setIsComposing(false);
      
      alert(`[${composeFrom}] 계정으로 메일이 성공적으로 발송되었습니다. (첨부파일 ${attachmentsToPass.length}개 포함)`);
    } catch (err) {
      console.error(err);
      alert('메일 발송 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  // Handle Eml / ZIP Backup File Select & Restore (Requirement 5)
  const handleBackupZipSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsRestoringBackup(true);
    setBackupRestoreProgress(10);
    try {
      const restoredEmails = await restoreFromZipBackup(file, (p) => setBackupRestoreProgress(p));
      if (restoredEmails.length > 0) {
        importRestoredEmails(restoredEmails);
        setBackupRestoreResult({ success: true, count: restoredEmails.length });
        if (restoredEmails[0]) {
          setSelectedEmail(restoredEmails[0]);
        }
      } else {
        alert('업로드하신 백업 파일 내에서 .eml 형식의 메일을 찾지 못했습니다.');
      }
    } catch (err) {
      alert(`백업 파일 복구 중 오류가 발생했습니다: ${err.message}`);
    } finally {
      setIsRestoringBackup(false);
      if (e.target) e.target.value = '';
    }
  };

  // Handle Export & Download ZIP Backup (Requirement 6)
  const handleExportBackup = async () => {
    try {
      setIsExportingBackup(true);
      await exportMailBackupZip(emails, currentUser);
    } catch (err) {
      alert(`메일 백업 생성 중 오류 발생: ${err.message}`);
    } finally {
      setIsExportingBackup(false);
    }
  };

  // Auto mark as read on email selection (Requirement 1)
  const handleSelectEmail = (email) => {
    setSelectedEmail({ ...email, IsRead: true });
    if (!email.IsRead) {
      markEmailsRead([email.EmailID], true);
    }
    setIsMobileDetailOpen(true);
  };

  // Keep selectedEmail synchronized with emails state updates
  useEffect(() => {
    if (selectedEmail) {
      const live = emails.find(e => e.EmailID === selectedEmail.EmailID);
      if (live && (live.IsRead !== selectedEmail.IsRead || live.IsRecipientRead !== selectedEmail.IsRecipientRead)) {
        setSelectedEmail(live);
      }
    }
  }, [emails]);

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-[#F4F5F9] min-h-[calc(100vh-80px)] text-slate-800 space-y-6 text-left">
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            {currentFolder === 'inbox' && '받은메일함'}
            {currentFolder === 'sent' && '보낸메일함'}
            {currentFolder === 'draft' && '임시보관함'}
            {currentFolder === 'starred' && '중요메일'}
            {currentFolder === 'spam' && '스팸메일함'}
            {currentFolder === 'trash' && '휴지통'}
          </h1>
          <p className="text-xs md:text-sm text-slate-500 mt-1 font-medium">사내 메일을 확인하고 AI 스마트 브리핑과 함께 안전하게 업무를 관리합니다.</p>
        </div>
        
        <div className="flex items-center gap-2.5">
          {/* Quick Layout Controls for PC */}
          <div className="hidden lg:flex items-center gap-1.5 bg-white border border-[#EAECF0] rounded-2xl p-1 shadow-card">
            <button
              type="button"
              onClick={() => setIsFolderCollapsed(!isFolderCollapsed)}
              className="p-2 text-slate-600 hover:text-[#5C5FEF] hover:bg-indigo-50/70 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
              title={isFolderCollapsed ? "사서함 분류 펼치기" : "사서함 분류 접기"}
            >
              {isFolderCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
              <span className="text-[11px]">{isFolderCollapsed ? "사서함 열기" : "사서함 접기"}</span>
            </button>
            <div className="w-px h-4 bg-slate-200" />
            <button
              type="button"
              onClick={handleResetPanelSizes}
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
              title="패널 크기 기본값 복원 (사서함 220px / 목록 380px)"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="text-[11px]">크기 초기화</span>
            </button>
          </div>

          <button
            onClick={() => handleOpenCompose({})}
            className="self-start sm:self-auto py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs md:text-sm rounded-xl shadow-xs flex items-center gap-1.5 cursor-pointer transition-all active:scale-98"
          >
            <Send className="w-4 h-4" />
            <span>메일 쓰기</span>
          </button>
        </div>
      </div>

      {/* Main Mail Grid Layout with Draggable Resizable Splitters */}
      <div className="flex flex-col lg:flex-row gap-0 h-[calc(100vh-210px)] overflow-hidden">
        
        {/* 1. Left Folder Sidebar */}
        {!isFolderCollapsed ? (
          <aside 
            style={{ width: `${folderWidth}px` }}
            className={`${isComposing ? 'hidden lg:flex' : 'flex'} w-full lg:w-auto flex-shrink-0 bg-white border border-[#EAECF0] rounded-2xl p-4 flex-col justify-between shadow-card overflow-y-auto`}
          >
          <div className="space-y-3">
            {/* Top Action Buttons */}
            <div className="grid grid-cols-2 gap-2 pb-1">
              <button
                type="button"
                onClick={() => handleOpenCompose({})}
                className="py-2.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-98"
              >
                <Send className="w-3.5 h-3.5" />
                <span>메일 쓰기</span>
              </button>

              <button
                type="button"
                onClick={() => handleOpenCompose({ isSendToSelf: true })}
                className="py-2.5 px-3 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs sm:text-sm rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-98"
              >
                <CornerUpRight className="w-3.5 h-3.5" />
                <span>내게 쓰기</span>
              </button>
            </div>

            {/* Quick Status Bar */}
            <div className="grid grid-cols-4 gap-1 p-2 bg-slate-50 border border-slate-200/80 rounded-xl text-center">
              <button
                type="button"
                onClick={() => {
                  handleSelectFolder('inbox');
                  setUnreadFilter('unread');
                }}
                className="flex flex-col items-center hover:text-indigo-600 cursor-pointer"
                title="안읽은 메일 보기"
              >
                <span className="text-xs font-black text-indigo-600">{unreadInboxCount}</span>
                <span className="text-[10px] text-slate-500 font-medium">안읽음</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectFolder('starred')}
                className="flex flex-col items-center hover:text-amber-600 cursor-pointer"
                title="중요 메일 보기"
              >
                <span className="text-xs font-black text-amber-500">{userStarredCount}</span>
                <span className="text-[10px] text-slate-500 font-medium">중요</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (currentFolder !== 'inbox') {
                    handleSelectFolder('inbox');
                  }
                  setAttachmentFilter(prev => !prev);
                }}
                className={`flex flex-col items-center cursor-pointer transition-all ${
                  attachmentFilter ? 'text-[#5C5FEF] font-bold scale-105' : 'hover:text-slate-900 text-slate-700'
                }`}
                title={attachmentFilter ? "첨부 필터 해제 (전체 보기)" : "첨부 파일 있는 메일만 보기"}
              >
                <span className={`text-xs font-black ${attachmentFilter ? 'text-[#5C5FEF] underline' : 'text-slate-700'}`}>{emailsWithAttachmentsCount}</span>
                <span className={`text-[10px] font-medium ${attachmentFilter ? 'text-[#5C5FEF] font-bold' : 'text-slate-500'}`}>첨부</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleSelectFolder('inbox');
                  setSearchTerm(currentUser?.Email || '');
                }}
                className="flex flex-col items-center hover:text-emerald-600 cursor-pointer"
                title="내게 쓴 메일"
              >
                <span className="text-xs font-black text-emerald-600">{selfEmailsCount}</span>
                <span className="text-[10px] text-slate-500 font-medium">내게쓴</span>
              </button>
            </div>

            <div className="space-y-1.5 pt-1">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block px-2 py-1">
                사서함 분류
              </span>

            {/* Received Mailbox (Requirement 1) */}
            <button
              onClick={() => handleSelectFolder('inbox')}
              className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                currentFolder === 'inbox'
                  ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold shadow-2xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-base">📥</span>
                <span>받은메일함</span>
              </div>
              <div className="flex items-center gap-1.5">
                {unreadInboxCount > 0 && (
                  <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-indigo-600 text-white shadow-2xs">
                    {unreadInboxCount}
                  </span>
                )}
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {totalInboxEmails.length}
                </span>
              </div>
            </button>

            {/* Sent Mailbox */}
            <button
              onClick={() => handleSelectFolder('sent')}
              className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                currentFolder === 'sent'
                  ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold shadow-2xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-base">📤</span>
                <span>보낸메일함</span>
              </div>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                {totalSentEmails.length}
              </span>
            </button>

            {/* Drafts Mailbox */}
            <button
              onClick={() => handleSelectFolder('draft')}
              className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                currentFolder === 'draft'
                  ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold shadow-2xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-base">📝</span>
                <span>임시보관함</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${drafts.length > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                {drafts.length}
              </span>
            </button>

            {/* Starred Mailbox */}
            <button
              onClick={() => handleSelectFolder('starred')}
              className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                currentFolder === 'starred'
                  ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold shadow-2xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-base text-amber-500">⭐</span>
                <span>중요메일</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${userStarredCount > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                {userStarredCount}
              </span>
            </button>

            {/* Spam Mailbox */}
            <button
              onClick={() => handleSelectFolder('spam')}
              className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                currentFolder === 'spam'
                  ? 'bg-rose-50 border border-rose-200 text-rose-700 font-bold shadow-2xs'
                  : 'text-slate-600 hover:text-rose-600 hover:bg-rose-50/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-base">🚫</span>
                <span>스팸메일함</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                totalSpamEmails.length > 0 ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-600'
              }`}>
                {totalSpamEmails.length}
              </span>
            </button>

            {/* Trash Mailbox */}
            {(() => {
              const trashCount = emails.filter(e => Boolean(trashEmailMap[e.EmailID]) || e.Folder === 'trash' || e.IsDeleted === true).length;
              return (
                <button
                  onClick={() => handleSelectFolder('trash')}
                  className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                    currentFolder === 'trash'
                      ? 'bg-slate-200 border border-slate-300 text-slate-900 font-bold shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base">🗑️</span>
                    <span>휴지통</span>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    trashCount > 0 ? 'bg-slate-200 text-slate-800 font-black' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {trashCount}
                  </span>
                </button>
              );
            })()}
          </div>

          {/* Custom Mailboxes Section */}
          <div className="pt-3 border-t border-slate-100 space-y-1 text-left">
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                사용자 정의 메일함
              </span>
              <button
                type="button"
                onClick={handleAddCustomFolder}
                className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5 cursor-pointer"
                title="새 메일함 추가"
              >
                <span>+ 메일함 추가</span>
              </button>
            </div>

            {customFolders.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400 text-center font-medium">
                등록된 메일함이 없습니다.
              </div>
            ) : (
              customFolders.map(folder => {
                const isActive = currentFolder === folder.id;
                const count = emails.filter(e => !isEmailTrash(e) && getEffectiveCustomFolder(e) === folder.id).length;

                return (
                  <div key={folder.id} className="group flex items-center justify-between p-0.5 rounded-xl hover:bg-slate-50">
                    <button
                      onClick={() => handleSelectFolder(folder.id)}
                      className={`flex-1 flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                        isActive
                          ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 font-black shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 overflow-hidden">
                        <span className="text-sm">📁</span>
                        <span className="truncate">{folder.name}</span>
                      </div>
                      <span className="text-[11px] font-bold px-2 py-0.2 rounded-full bg-slate-100 text-slate-700">
                        {count}
                      </span>
                    </button>

                    <div className="hidden group-hover:flex items-center gap-0.5 pr-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRenameCustomFolder(folder.id, folder.name); }}
                        className="p-1 text-slate-400 hover:text-amber-700 rounded cursor-pointer"
                        title="메일함 이름 수정"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCustomFolder(folder.id, folder.name); }}
                        className="p-1 text-slate-400 hover:text-rose-600 rounded cursor-pointer"
                        title="메일함 삭제"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Mail Backup & Restore Center (Requirement 5 & 6) */}
          <div className="pt-4 border-t border-slate-100 space-y-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block px-1">
              메일 아카이브 & 백업
            </span>
            <input
              type="file"
              ref={zipFileInputRef}
              onChange={handleBackupZipSelect}
              accept=".zip,.eml"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => zipFileInputRef.current?.click()}
              disabled={isRestoringBackup}
              className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-200 text-slate-700 hover:text-indigo-700 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs"
              title="list.html 및 messages/*.eml 포함된 ZIP 백업 파일 복구"
            >
              <div className="flex items-center gap-2">
                <PackageOpen className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                <span>백업 복구 (ZIP/EML)</span>
              </div>
              <span className="text-[10px] text-slate-400 font-normal">가져오기</span>
            </button>

            <button
              type="button"
              onClick={handleExportBackup}
              disabled={isExportingBackup}
              className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs"
              title="모든 메일을 list.html 및 .eml 패키지로 압축 다운로드"
            >
              <div className="flex items-center gap-2">
                <FolderArchive className="w-4 h-4 text-slate-600 flex-shrink-0" />
                <span>메일 백업 (ZIP)</span>
              </div>
              <span className="text-[10px] text-slate-400 font-normal">{isExportingBackup ? '생성 중...' : '다운로드'}</span>
            </button>
          </div>
          </div>

          <div className="pt-4 border-t border-slate-100 text-xs text-slate-500 font-medium space-y-1.5">
            <div className="flex justify-between items-center text-[11px]">
              <span className="text-slate-600 font-bold">스토리지 사용량</span>
              <span className="font-bold text-slate-800">
                {storageInfo.usedMB} MB / {storageInfo.quotaGB} GB
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div 
                className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                style={{ width: `${Math.max(storageInfo.usagePercent, 2)}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-[10px] text-slate-400">
              <span>{storageInfo.usagePercent}% 사용 중</span>
              <span>총 {storageInfo.emailCount}개 메일</span>
            </div>
          </div>
        </aside>
        ) : (
          <div className="hidden lg:flex flex-col items-center py-4 px-2 bg-white border border-slate-200 rounded-2xl shadow-xs self-stretch justify-start gap-3 flex-shrink-0 mr-2">
            <button
              type="button"
              onClick={() => setIsFolderCollapsed(false)}
              className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl cursor-pointer transition-all"
              title="사서함 분류 펼치기"
            >
              <PanelLeft className="w-5 h-5" />
            </button>
            <span className="text-[10px] font-bold text-slate-400 [writing-mode:vertical-rl] tracking-widest uppercase">
              사서함
            </span>
          </div>
        )}

        {/* Resizer Divider 1 (Folder <-> List) */}
        {!isFolderCollapsed && (
          <div
            onMouseDown={handleFolderMouseDown}
            className="hidden lg:flex w-3.5 -mx-1 items-center justify-center cursor-col-resize z-20 group select-none py-6 self-stretch hover:bg-indigo-50/50 transition-colors rounded-lg"
            title="마우스로 좌우 드래그하여 사서함 너비 조절"
          >
            <div className={`w-1 h-14 rounded-full transition-all ${
              isDraggingFolder ? 'bg-indigo-600 h-20 w-1.5' : 'bg-slate-300 group-hover:bg-indigo-400'
            }`} />
          </div>
        )}

        {/* Main Area: Either Inline Full-page Compose View OR Email List & Detail View */}
        {isComposing ? (
          <div className="flex-1 bg-white border border-slate-200 rounded-2xl flex flex-col overflow-hidden shadow-xs relative text-left">
            {/* Sticky Top Header */}
            <div className="px-4 sm:px-6 py-3 border-b border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 flex-shrink-0">
              <div className="flex items-center gap-3">
                <h2 className="text-lg md:text-xl font-black text-slate-900 tracking-tight">
                  {activeDraftId ? '임시보관 메일 수정' : (forwardQuotedEmail ? (composeSubject.startsWith('FW') || composeSubject.startsWith('Fwd') ? '메일 전달' : '답장 쓰기') : (isSendToSelf ? '내게 쓰기' : '메일 쓰기'))}
                </h2>
                {drafts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (composeSubject || composeBody || composeTo) {
                        if (!confirm('작성 중인 내용이 있습니다. 임시보관함으로 이동하시겠습니까?')) return;
                      }
                      setIsComposing(false);
                      setCurrentFolder('draft');
                    }}
                    className="text-xs text-slate-500 hover:text-indigo-600 font-bold bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                  >
                    임시보관 메일 {drafts.length}
                  </button>
                )}
              </div>

              {/* Actions Toolbar */}
              <div className="flex items-center flex-wrap gap-2">
                <button
                  type="button"
                  id="compose-send-btn"
                  disabled={isSending || attachedFiles.some(f => f.isUploading)}
                  onClick={handleSend}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs md:text-sm rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed active:scale-98"
                  title={attachedFiles.some(f => f.isUploading) ? '첨부파일 업로드가 완료된 후 발송 가능합니다' : ''}
                >
                  {isSending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : attachedFiles.some(f => f.isUploading) ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span>
                    {isSending 
                      ? '발송 중...' 
                      : attachedFiles.some(f => f.isUploading) 
                        ? '파일 업로드 중...' 
                        : '보내기'}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs md:text-sm rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
                  title="현재 작성 내용 임시보관함에 저장"
                >
                  <Save className="w-3.5 h-3.5 text-slate-600" />
                  <span>임시저장</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowComposePreview(true)}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs md:text-sm rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
                  title="발송 전 미리보기"
                >
                  <Eye className="w-3.5 h-3.5 text-slate-600" />
                  <span>미리보기</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const nextSelf = !isSendToSelf;
                    setIsSendToSelf(nextSelf);
                    if (nextSelf) {
                      setComposeTo(primaryAlias || currentUser?.Email || '');
                      setShowRecipientDropdown(false);
                    } else {
                      if (composeTo === (primaryAlias || currentUser?.Email || '')) {
                        setComposeTo('');
                      }
                    }
                  }}
                  className={`px-3 py-2 border rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    isSendToSelf
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                      : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                  title="내 대표 이메일로 수신자 자동 지정 토글"
                >
                  <CornerUpRight className="w-3.5 h-3.5 text-emerald-600" />
                  <span>내게쓰기</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (composeSubject || composeBody || composeTo) {
                      if (!confirm('작성을 취소하고 목록으로 돌아가시겠습니까? (작성 중인 내용은 저장되지 않습니다)')) {
                        return;
                      }
                    }
                    setIsComposing(false);
                  }}
                  className="px-3 py-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                  title="작성 취소 및 목록으로 복귀"
                >
                  <X className="w-4 h-4" />
                  <span>취소</span>
                </button>
              </div>
            </div>

            {/* Form Fields & CKEditor Scrollable Body */}
            <form
              onSubmit={handleSend}
              className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3.5 text-left relative"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Drag & Drop Overlay */}
              {isDraggingFile && (
                <div className="absolute inset-0 z-50 bg-indigo-600/20 backdrop-blur-[2px] border-4 border-dashed border-indigo-600 rounded-2xl flex flex-col items-center justify-center pointer-events-none animate-in fade-in duration-150">
                  <div className="bg-white/95 border border-indigo-200 p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-2">
                    <UploadCloud className="w-12 h-12 text-indigo-600 animate-bounce" />
                    <p className="text-base font-bold text-slate-900">파일을 여기에 놓으면 첨부됩니다</p>
                    <p className="text-xs text-slate-500 font-medium">한글(HWP), 엑셀, PPT, Word, PDF, 이미지 등 모든 파일 첨부 가능</p>
                  </div>
                </div>
              )}

              {/* Sender / From */}
              <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 text-xs md:text-sm">
                <label className="md:col-span-2 font-bold text-slate-600">보내는사람</label>
                <div className="md:col-span-10">
                  {availableSenderEmails.length > 1 ? (
                    <select
                      value={composeFrom}
                      onChange={e => setComposeFrom(e.target.value)}
                      className="w-full max-w-md px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs sm:text-sm font-semibold text-slate-800 focus:outline-none focus:border-indigo-600 cursor-pointer shadow-2xs"
                    >
                      {availableSenderEmails.map((email, idx) => (
                        <option key={idx} value={email}>
                          {email} {email === defaultSenderEmail ? '(기본 발신 계정)' : email === primaryAlias ? '(대표 별칭)' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-xs sm:text-sm font-semibold text-slate-800">
                      {composeFrom || currentUser.Email}
                      {userAliases.length > 0 && (
                        <span className="text-[10px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-bold ml-2">대표 별칭</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Recipient / To */}
              <div className="grid grid-cols-1 md:grid-cols-12 items-start gap-2 text-xs md:text-sm">
                <div className="md:col-span-2 pt-2.5 flex items-center justify-between">
                  <label className="font-bold text-slate-600">받는사람</label>
                </div>
                <div className="md:col-span-10 space-y-1.5 relative">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        id="compose-to-input"
                        placeholder={isSendToSelf ? '내 대표 이메일로 발송됩니다' : '이름(직원) 또는 이메일 주소 입력 후 Enter...'}
                        value={composeTo}
                        readOnly={isSendToSelf}
                        onFocus={() => { if (!isSendToSelf) setShowRecipientDropdown(true); }}
                        onChange={(e) => {
                          if (!isSendToSelf) {
                            setComposeTo(e.target.value);
                            setShowRecipientDropdown(true);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (!isSendToSelf) {
                            handleRecipientInputKeyDown(e, composeTo, setComposeTo, { employees: matchingEmployees, contacts: matchingContacts }, setShowRecipientDropdown);
                          }
                        }}
                        className={`w-full px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                          isSendToSelf 
                            ? 'bg-emerald-50/60 border border-emerald-200 text-emerald-950 font-bold select-none cursor-default' 
                            : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600'
                        }`}
                        required
                      />
                      {composeTo && !isSendToSelf && (
                        <button
                          type="button"
                          onClick={() => setComposeTo('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowCcBcc(prev => !prev)}
                      className="px-3 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 cursor-pointer whitespace-nowrap"
                    >
                      {showCcBcc ? '참조 접기 ▲' : '참조 추가 ▼'}
                    </button>
                  </div>

                  {/* Autocomplete Dropdown */}
                  {!isSendToSelf && showRecipientDropdown && (matchingEmployees.length > 0 || matchingContacts.length > 0) && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-64 overflow-y-auto divide-y divide-slate-100 text-left">
                      {matchingEmployees.length > 0 && (
                        <div>
                          <div className="px-3.5 py-1.5 bg-slate-50 text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between border-b border-slate-100">
                            <span>🏢 사내 임직원 검색 결과 (Enter 자동완성)</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowRecipientDropdown(false);
                              }}
                              className="text-slate-400 hover:text-slate-700 font-bold px-1.5 py-0.5 rounded text-[11px] hover:bg-slate-200/60 cursor-pointer"
                            >
                              ✕ 닫기
                            </button>
                          </div>
                          {matchingEmployees.map(emp => (
                            <div
                              key={emp.UserID}
                              onClick={() => {
                                const tokens = composeTo.split(',').map(t => t.trim()).filter(Boolean);
                                tokens.pop();
                                tokens.push(emp.Email);
                                setComposeTo(tokens.join(', ') + ', ');
                                setShowRecipientDropdown(false);
                              }}
                              className="px-3.5 py-2.5 hover:bg-indigo-50/70 cursor-pointer flex items-center justify-between transition-colors"
                            >
                              <div className="flex items-center gap-2.5 truncate">
                                <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                  {emp.Name.charAt(0)}
                                </div>
                                <div className="truncate">
                                  <div className="flex items-center gap-1.5">
                                    <strong className="text-sm text-slate-900">{emp.Name}</strong>
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">{emp.Department}</span>
                                    <span className="text-xs text-indigo-600 font-medium">{emp.JobTitle}</span>
                                  </div>
                                  <span className="text-xs text-slate-500 font-mono block">{emp.Email}</span>
                                </div>
                              </div>
                              <span className="text-xs font-bold text-indigo-600 px-2 py-1 rounded-lg bg-indigo-50 border border-indigo-200 flex-shrink-0">
                                +추가
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {matchingContacts.length > 0 && (
                        <div>
                          <div className="px-3.5 py-1.5 bg-slate-50 text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between border-b border-slate-100">
                            <span>✉ 최근 발신 및 주소록 연락처</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowRecipientDropdown(false);
                              }}
                              className="text-slate-400 hover:text-slate-700 font-bold px-1.5 py-0.5 rounded text-[11px] hover:bg-slate-200/60 cursor-pointer"
                            >
                              ✕ 닫기
                            </button>
                          </div>
                          {matchingContacts.map((contact, idx) => (
                            <div
                              key={idx}
                              onClick={() => {
                                const tokens = composeTo.split(',').map(t => t.trim()).filter(Boolean);
                                tokens.pop();
                                tokens.push(contact.Email);
                                setComposeTo(tokens.join(', ') + ', ');
                                setShowRecipientDropdown(false);
                              }}
                              className="px-3.5 py-2.5 hover:bg-slate-50 cursor-pointer flex items-center justify-between transition-colors"
                            >
                              <div className="truncate">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-semibold text-slate-800">{contact.Name}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{contact.Source}</span>
                                </div>
                                <span className="text-xs text-slate-500 font-mono block">{contact.Email}</span>
                              </div>
                              <span className="text-xs text-slate-400 font-medium flex-shrink-0">
                                +추가 ❯
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* CC & BCC (Expandable) */}
              {showCcBcc && (
                <div className="grid grid-cols-1 md:grid-cols-12 items-start gap-2 text-xs md:text-sm">
                  <label className="md:col-span-2 pt-2.5 font-bold text-slate-600">참조/숨은참조</label>
                  <div className="md:col-span-10 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-2xl border border-slate-200">
                    <div className="space-y-1 relative">
                      <label className="text-[11px] font-bold text-slate-600 uppercase">참조 (CC)</label>
                      <input
                        type="text"
                        placeholder="참조 이메일 검색 (Enter 입력)"
                        value={composeCc}
                        onChange={(e) => {
                          setComposeCc(e.target.value);
                          setShowCcDropdown(true);
                        }}
                        onFocus={() => setShowCcDropdown(true)}
                        onKeyDown={(e) => handleRecipientInputKeyDown(e, composeCc, setComposeCc, matchingCc, setShowCcDropdown)}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-600"
                      />
                      {showCcDropdown && (matchingCc.employees.length > 0 || matchingCc.contacts.length > 0) && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto divide-y divide-slate-100 text-left">
                          <div className="px-3 py-1 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase flex items-center justify-between border-b border-slate-100">
                            <span>참조 검색 결과</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCcDropdown(false);
                              }}
                              className="text-slate-400 hover:text-slate-700 font-bold px-1 rounded cursor-pointer"
                            >
                              ✕ 닫기
                            </button>
                          </div>
                          {matchingCc.employees.map(emp => (
                            <div
                              key={emp.UserID}
                              onClick={() => {
                                const tokens = composeCc.split(',').map(t => t.trim()).filter(Boolean);
                                tokens.pop();
                                tokens.push(emp.Email);
                                setComposeCc(tokens.join(', ') + ', ');
                                setShowCcDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-indigo-50 cursor-pointer flex items-center justify-between text-xs"
                            >
                              <div>
                                <span className="font-bold text-slate-900">{emp.Name}</span>
                                <span className="text-slate-500 ml-1 text-[11px]">({emp.Department})</span>
                                <span className="text-slate-400 block font-mono text-[10px]">{emp.Email}</span>
                              </div>
                              <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-bold">추가</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-1 relative">
                      <label className="text-[11px] font-bold text-slate-600 uppercase">숨은 참조 (BCC)</label>
                      <input
                        type="text"
                        placeholder="숨은 참조 이메일 검색 (Enter 입력)"
                        value={composeBcc}
                        onChange={(e) => {
                          setComposeBcc(e.target.value);
                          setShowBccDropdown(true);
                        }}
                        onFocus={() => setShowBccDropdown(true)}
                        onKeyDown={(e) => handleRecipientInputKeyDown(e, composeBcc, setComposeBcc, matchingBcc, setShowBccDropdown)}
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-600"
                      />
                      {showBccDropdown && (matchingBcc.employees.length > 0 || matchingBcc.contacts.length > 0) && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto divide-y divide-slate-100 text-left">
                          <div className="px-3 py-1 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase flex items-center justify-between border-b border-slate-100">
                            <span>숨은 참조 검색 결과</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowBccDropdown(false);
                              }}
                              className="text-slate-400 hover:text-slate-700 font-bold px-1 rounded cursor-pointer"
                            >
                              ✕ 닫기
                            </button>
                          </div>
                          {matchingBcc.employees.map(emp => (
                            <div
                              key={emp.UserID}
                              onClick={() => {
                                const tokens = composeBcc.split(',').map(t => t.trim()).filter(Boolean);
                                tokens.pop();
                                tokens.push(emp.Email);
                                setComposeBcc(tokens.join(', ') + ', ');
                                setShowBccDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-indigo-50 cursor-pointer flex items-center justify-between text-xs"
                            >
                              <div>
                                <span className="font-bold text-slate-900">{emp.Name}</span>
                                <span className="text-slate-500 ml-1 text-[11px]">({emp.Department})</span>
                                <span className="text-slate-400 block font-mono text-[10px]">{emp.Email}</span>
                              </div>
                              <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-bold">추가</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 text-xs md:text-sm">
                <div className="md:col-span-2 flex items-center gap-2">
                  <label className="font-bold text-slate-600">제목</label>
                  <label className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isImportant}
                      onChange={(e) => setIsImportant(e.target.checked)}
                      className="w-3.5 h-3.5 rounded text-rose-600 focus:ring-rose-500 border-slate-300 cursor-pointer"
                    />
                    <span>중요 !</span>
                  </label>
                </div>
                <div className="md:col-span-10">
                  <input
                    type="text"
                    id="compose-subject-input"
                    placeholder="메일 제목을 입력하세요"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 font-medium"
                    required
                  />
                </div>
              </div>

              {/* File Attachments */}
              <div className="grid grid-cols-1 md:grid-cols-12 items-start gap-2 text-xs md:text-sm">
                <label className="md:col-span-2 pt-2.5 font-bold text-slate-600">파일첨부</label>
                <div className="md:col-span-10 space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-all shadow-2xs"
                      >
                        <Paperclip className="w-3.5 h-3.5 text-slate-600" />
                        <span>내 PC</span>
                      </button>
                    </div>
                    <span className="text-[11px] text-slate-400 font-mono font-medium">
                      일반 {formatFileSize(attachedFiles.reduce((acc, f) => acc + (f.size || 0), 0))} / 25MB (최대 10개)
                    </span>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    className="hidden"
                  />

                  {/* Drag & drop dropzone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-200 hover:border-indigo-400 bg-slate-50/60 hover:bg-indigo-50/20 p-3.5 rounded-2xl text-center cursor-pointer transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-600 font-semibold">
                      파일을 마우스로 끌어 오거나 클릭하여 첨부하세요 (한글 HWP, 엑셀, PDF, 이미지 등 지원)
                    </span>
                  </div>

                  {/* Attached Files List */}
                  {attachedFiles.length > 0 && (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl space-y-2 max-h-48 overflow-y-auto">
                      {attachedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-xl shadow-2xs text-xs"
                        >
                          <div className="flex items-center gap-2.5 truncate">
                            <FileText className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span className="font-semibold text-slate-900 truncate max-w-xs">{file.name}</span>
                            <span className="text-[10px] text-slate-400">({formatFileSize(file.size)})</span>
                            {file.isUploading && (
                              <span className="text-[10px] text-indigo-600 animate-pulse font-bold ml-1">업로드 중...</span>
                            )}
                            {file.error && (
                              <span className="text-[10px] text-rose-600 font-bold ml-1">{file.error}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachedFile(idx)}
                            className="text-slate-400 hover:text-rose-600 p-1 rounded-lg transition-colors cursor-pointer"
                            title="첨부 파일 삭제"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* CKEditor 5 Body */}
              <div className="space-y-2 pt-2">
                <CKMailEditor
                  content={composeBody}
                  onChange={(html, text) => {
                    setComposeBody(html);
                  }}
                  placeholder={forwardQuotedEmail ? "전달/답장 추가 메시지를 작성해 주세요... (비워둘 시 원본 메일만 발송)" : "메일 본문 내용을 작성하세요. (이미지 붙여넣기, 표, 서식 등 지원)"}
                  minHeight="350px"
                />
              </div>

              {/* KakaoTalk Notification Option */}
              <div 
                id="kakao-notice-toggle-btn"
                onClick={() => setSendKakaoNotice(prev => !prev)}
                className="bg-amber-50/80 border border-amber-200/90 p-3.5 rounded-2xl flex items-center justify-between shadow-2xs cursor-pointer hover:bg-amber-100/60 transition-all select-none"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-amber-400 text-slate-950 flex items-center justify-center font-bold shadow-2xs flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-slate-900" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-slate-900 block">수신자에게 카카오톡 도착 알림 동시 전송</span>
                    <span className="text-[11px] text-slate-600 font-medium">메일 발송 시 사내 임직원 수신자에게 카카오톡으로 메일 제목과 확인 안내 알림을 즉시 발송합니다.</span>
                  </div>
                </div>
                <div className="relative inline-flex items-center flex-shrink-0 ml-3">
                  <input
                    type="checkbox"
                    checked={sendKakaoNotice}
                    onChange={e => setSendKakaoNotice(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-10 h-5.5 rounded-full transition-all relative ${sendKakaoNotice ? 'bg-amber-500' : 'bg-slate-300'}`}>
                    <div className={`w-4.5 h-4.5 bg-white rounded-full absolute top-[2px] transition-all shadow-xs ${sendKakaoNotice ? 'right-[2px]' : 'left-[2px]'}`} />
                  </div>
                </div>
              </div>

              {/* Automatic Signature Preview */}
              <div className="border border-slate-200 p-4 rounded-2xl bg-slate-50 space-y-2">
                <span className="text-xs font-bold text-slate-600 uppercase">발신자 명함 서명 (자동 첨부됨):</span>
                <div
                  className="bg-white p-3.5 rounded-xl border border-slate-200 text-xs"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(getUserSignature(currentUser)) }}
                />
              </div>

              {/* Bottom Actions Bar */}
              <div className="pt-4 border-t border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={isSending || attachedFiles.some(f => f.isUploading)}
                    onClick={handleSend}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs md:text-sm rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed active:scale-98"
                    title={attachedFiles.some(f => f.isUploading) ? '첨부파일 업로드가 완료된 후 발송 가능합니다' : ''}
                  >
                    {isSending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : attachedFiles.some(f => f.isUploading) ? (
                      <Loader2 className="w-4 h-4 animate-spin text-amber-300" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    <span>
                      {isSending 
                        ? '발송 중...' 
                        : attachedFiles.some(f => f.isUploading) 
                          ? '파일 업로드 중...' 
                          : '보내기'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs md:text-sm rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <Save className="w-4 h-4" />
                    <span>임시저장</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (composeSubject || composeBody || composeTo) {
                      if (!confirm('작성을 취소하고 목록으로 돌아가시겠습니까?')) return;
                    }
                    setIsComposing(false);
                  }}
                  className="px-4 py-2.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  취소 / 목록으로
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {/* 2. Middle Email List Pane */}
            <div 
              style={mailViewLayout === 'split' ? { width: `${listWidth}px` } : {}}
              className={`${
                mailViewLayout === 'full' && selectedEmail ? 'hidden' : 'flex'
              } ${
                mailViewLayout === 'full' ? 'flex-1 w-full' : 'w-full lg:w-auto flex-shrink-0'
              } bg-white border border-slate-200 rounded-2xl flex-col overflow-hidden shadow-xs transition-all`}
            >
          {/* Pinned Resignation D-Day Guidance Banner for approved resignations */}
          {resignationDDayInfo && (
            <div className="bg-gradient-to-r from-rose-900 to-rose-950 text-white p-3 border-b border-rose-700/50 flex items-center justify-between gap-2 text-left">
              <div className="flex items-center gap-2 overflow-hidden">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider flex-shrink-0 ${resignationDDayInfo.badgeClass}`}>
                  {resignationDDayInfo.dDayLabel}
                </span>
                <div className="truncate">
                  <span className="text-xs font-bold text-rose-100 block truncate">
                    사직원 최종 승인 완료 ({resignationDDayInfo.resignationDate} 퇴사)
                  </span>
                  <span className="text-[10px] text-rose-200/80 block truncate">
                    {resignationDDayInfo.subMessage}
                  </span>
                </div>
              </div>
              <a
                href="#/contract"
                className="px-2.5 py-1 bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold rounded-lg transition-colors flex-shrink-0 whitespace-nowrap shadow-xs"
              >
                문서보기
              </a>
            </div>
          )}

          {/* Search Header */}
          <div className="p-3.5 border-b border-slate-100 space-y-2.5">
            <div className="relative">
              <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="메일 검색 (보낸이, 제목, 본문)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:border-indigo-500 focus:bg-white text-slate-900 placeholder-slate-400 transition-all"
              />
            </div>

            {/* Trash Retention & Info Banner (Requirement 5) */}
            {currentFolder === 'trash' && (
              <div className="bg-slate-100 border border-slate-200 p-2.5 rounded-xl text-xs flex items-center justify-between text-slate-700 font-bold">
                <div className="flex items-center gap-1.5">
                  <Trash2 className="w-4 h-4 text-slate-500" />
                  <span>
                    휴지통 유효기간:
                  </span>
                  <select
                    value={trashRetentionDays}
                    onChange={(e) => saveTrashRetentionDays(Number(e.target.value))}
                    className="bg-white border border-slate-300 text-slate-900 rounded-lg px-2 py-0.5 text-xs font-extrabold focus:outline-none focus:border-indigo-600"
                  >
                    <option value={1}>1일</option>
                    <option value={3}>3일</option>
                    <option value={7}>7일 (기본값)</option>
                    <option value={14}>14일</option>
                    <option value={30}>30일</option>
                  </select>
                  <span className="text-[11px] text-slate-400 font-normal">후 자동 정제</span>
                </div>

                <button
                  type="button"
                  onClick={handleEmptyTrash}
                  className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer shadow-2xs"
                >
                  휴지통 비우기
                </button>
              </div>
            )}

            {/* Folder Sub-Toolbar: Unread Filter Tabs / Select-All & Bulk Action Bar */}
            {selectedEmailIds.length > 0 ? (
              /* Multi-selection Bulk Action Bar */
              <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 p-2 rounded-xl text-xs animate-in fade-in">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedEmailIds.length === filteredEmails.length && filteredEmails.length > 0}
                    onChange={handleSelectAllToggle}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer ml-1"
                  />
                  <span className="font-extrabold text-indigo-900">{selectedEmailIds.length}개 선택</span>
                </div>
                <div className="flex items-center gap-1">
                  {currentFolder === 'trash' ? (
                    <button
                      type="button"
                      onClick={() => handleRestoreFromTrash(selectedEmailIds)}
                      className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold shadow-2xs cursor-pointer flex items-center gap-1"
                      title="받은메일함으로 복원"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>복원</span>
                    </button>
                  ) : (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          promptMoveToCustomFolder(selectedEmailIds, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="bg-white border border-slate-300 text-slate-800 rounded-lg px-2 py-1 text-xs font-bold shadow-2xs focus:outline-none cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>📁 메일함 이동...</option>
                      <option value="inbox">📥 받은메일함</option>
                      {customFolders.map(f => (
                        <option key={f.id} value={f.id}>📁 {f.name}</option>
                      ))}
                    </select>
                  )}

                  <button
                    type="button"
                    onClick={() => handleBatchMarkRead(true)}
                    className="p-1.5 bg-white hover:bg-slate-100 text-slate-700 rounded-lg shadow-2xs font-bold cursor-pointer"
                    title="읽음 처리"
                  >
                    <MailOpen className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBatchMarkRead(false)}
                    className="p-1.5 bg-white hover:bg-slate-100 text-slate-700 rounded-lg shadow-2xs font-bold cursor-pointer"
                    title="안읽음으로 표시"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleBatchSpam}
                    className="p-1.5 bg-white hover:bg-rose-50 text-rose-600 rounded-lg shadow-2xs font-bold cursor-pointer"
                    title="스팸 신고 및 차단"
                  >
                    <Ban className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleBatchDelete}
                    className="p-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg shadow-2xs font-bold cursor-pointer"
                    title={currentFolder === 'trash' ? '영구 삭제' : '휴지통으로 이동'}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedEmailIds([])}
                    className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                    title="선택 해제"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              /* Regular Filter Bar (Requirement 1) */
              <div className="flex items-center justify-between pt-0.5">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedEmailIds.length === filteredEmails.length && filteredEmails.length > 0}
                    onChange={handleSelectAllToggle}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer ml-1"
                    title="전체 선택"
                  />
                  {currentFolder === 'inbox' && (
                    <div className="flex bg-slate-100 p-0.5 rounded-lg text-xs font-bold">
                      <button
                        type="button"
                        onClick={() => setUnreadFilter('all')}
                        className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                          unreadFilter === 'all' ? 'bg-white text-indigo-700 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        전체 ({totalInboxEmails.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setUnreadFilter('unread')}
                        className={`px-2.5 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                          unreadFilter === 'unread' ? 'bg-white text-indigo-700 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        {unreadInboxCount > 0 && (
                          <span className="w-2 h-2 rounded-full bg-indigo-600" />
                        )}
                        <span>안읽은 메일 ({unreadInboxCount})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAttachmentFilter(prev => !prev)}
                        className={`px-2.5 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 ${
                          attachmentFilter ? 'bg-[#5C5FEF] text-white shadow-2xs font-bold' : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        <Paperclip className="w-3 h-3" />
                        <span>첨부 ({emailsWithAttachmentsCount})</span>
                      </button>
                    </div>
                  )}

                  {currentFolder === 'spam' && (
                    <span className="text-xs font-bold text-rose-700 flex items-center gap-1">
                      <Ban className="w-3.5 h-3.5" />
                      스팸 메일함 ({totalSpamEmails.length}개)
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {currentFolder === 'spam' && totalSpamEmails.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('스팸메일함의 모든 메일을 영구히 비우시겠습니까?')) {
                          emptySpamFolder();
                          setSelectedEmail(null);
                        }
                      }}
                      className="text-[11px] text-rose-600 hover:underline font-bold cursor-pointer mr-1"
                    >
                      스팸 비우기
                    </button>
                  )}

                  {/* Layout Mode Toggle Button */}
                  <button
                    type="button"
                    onClick={() => setMailViewLayout(prev => prev === 'full' ? 'split' : 'full')}
                    className={`hidden lg:flex px-2.5 py-1 rounded-lg text-xs font-bold border transition-all items-center gap-1.5 cursor-pointer shadow-2xs ${
                      mailViewLayout === 'full'
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    title={mailViewLayout === 'full' ? '현재 전체화면 모드 (클릭 시 2분할창 모드로 전환)' : '현재 분할창 모드 (클릭 시 전체화면 모드로 전환)'}
                  >
                    <Layout className="w-3.5 h-3.5" />
                    <span>{mailViewLayout === 'full' ? '전체화면' : '분할창'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Email Item Rows or Draft Rows with Scroll Pagination */}
          <div 
            onScroll={handleScrollEmailList}
            className="flex-1 overflow-y-auto divide-y divide-slate-100"
          >
            {isSearchingDb && (
              <div className="p-2 bg-indigo-50 text-indigo-700 text-xs font-bold text-center flex items-center justify-center gap-1.5 animate-pulse">
                <span>🔍 DynamoDB 전체 대상 실시간 원격 검색 중...</span>
              </div>
            )}

            {currentFolder === 'draft' ? (
              drafts.length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-sm">임시보관된 메일이 없습니다.</div>
              ) : (
                drafts.map((draft) => (
                  <div
                    key={draft.DraftID}
                    onClick={() => handleOpenDraft(draft)}
                    className="p-4 text-left cursor-pointer hover:bg-slate-50 transition-all space-y-1.5"
                  >
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-bold text-slate-900 truncate">
                        {draft.Recipient ? `받는이: ${draft.Recipient}` : '(받는 사람 미지정)'}
                      </span>
                      <span className="text-xs text-slate-400 flex-shrink-0 ml-2 font-mono">
                        {formatMailDateTime(draft.UpdatedAt)}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {draft.Subject}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {stripHtmlTags(draft.BodyText || draft.BodyHTML) || '(내용 없음)'}
                    </p>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-xs font-bold text-indigo-600 flex items-center gap-1">
                        ✏ 수정 및 이어서 작성 ❯
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm('이 임시보관 메일을 삭제하시겠습니까?')) {
                            deleteDraft(draft.DraftID);
                          }
                        }}
                        className="text-slate-400 hover:text-rose-600 p-1 rounded-lg cursor-pointer"
                        title="임시 메일 삭제"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )
            ) : filteredEmails.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-sm">
                {isSearchingDb ? '검색 중입니다...' : (unreadFilter === 'unread' ? '안읽은 메일이 없습니다.' : '메일이 없습니다.')}
              </div>
            ) : (
              <>
                {displayedEmails.map((email, idx) => {
                  const isSelected = selectedEmail?.EmailID === email.EmailID;
                  const isStarred = starredEmailIds.includes(email.EmailID);
                  return (
                    <div
                      key={email.EmailID}
                      onClick={() => handleSelectEmail(email)}
                      className={`p-3.5 text-left cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-indigo-50/70 border-l-4 border-indigo-600'
                          : 'hover:bg-slate-50'
                      } ${!email.IsRead ? 'bg-indigo-50/20' : ''}`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2 overflow-hidden">
                          {/* Checkbox with Shift+Click Range Selection (Requirement 2) */}
                          <input
                            type="checkbox"
                            checked={selectedEmailIds.includes(email.EmailID)}
                            onClick={(e) => handleCheckboxClick(e, email.EmailID, idx)}
                            onChange={() => {}}
                            className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer flex-shrink-0"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleStarEmail(email.EmailID);
                            }}
                            className="text-base cursor-pointer hover:scale-125 transition-transform flex-shrink-0"
                            title="중요메일 표시/해제"
                          >
                            {isStarred ? <span className="text-amber-500">★</span> : <span className="text-slate-300 hover:text-amber-400">☆</span>}
                          </button>
                          {!email.IsRead && currentFolder !== 'sent' && (
                            <span className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0" title="안읽음" />
                          )}
                          <Avatar config={getSenderAvatarConfig(email.Sender)} size={26} className="flex-shrink-0" />
                          <span className={`text-xs sm:text-sm truncate ${!email.IsRead && currentFolder !== 'sent' ? 'font-extrabold text-slate-950' : 'font-semibold text-slate-800'}`}>
                            {currentFolder === 'inbox' || currentFolder === 'starred' || currentFolder === 'spam' ? email.Sender : `받는사람: ${email.Recipient}`}
                          </span>
                          {getGroupMailInfo(email) && (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 inline-flex items-center gap-1 flex-shrink-0">
                              👥 {getGroupMailInfo(email).GroupEmail.split('@')[0]} 그룹 수신
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-slate-400 flex-shrink-0 ml-2 font-mono">
                          {formatMailDateTime(email.Timestamp || email.DeliveredAt || email.Date)}
                        </span>
                      </div>

                      <p className={`text-xs sm:text-sm truncate mb-0.5 pl-14 ${!email.IsRead && currentFolder !== 'sent' ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                        {email.Subject}
                      </p>
                      <p className="text-xs text-slate-500 truncate pl-14">
                        {stripHtmlTags(email.BodyText || email.BodyHTML) || '(본문 없음)'}
                      </p>

                      {/* Read Receipt (수신 확인) Badge for Sent folder (Requirement 2) */}
                      {currentFolder === 'sent' && (
                        <div className="mt-2 flex items-center justify-between pl-6 text-xs">
                          {!isInternalRecipient(email.Recipient) ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-400 border border-slate-200">
                              <span>외부 메일 수신확인 불가</span>
                            </span>
                          ) : email.IsRecipientRead ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                              <CheckCheck className="w-3 h-3 text-emerald-600" />
                              <span>읽음 {email.ReadAt ? `(${email.ReadAt.slice(-5)})` : ''}</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                              <Clock className="w-3 h-3 text-slate-400" />
                              <span>읽지않음</span>
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 font-mono">발송 완료</span>
                        </div>
                      )}

                      {/* Quick Detail Bar for Mobile */}
                      <div className="mt-2 flex items-center justify-between lg:hidden pt-2 border-t border-slate-100 pl-6">
                        <span className="text-xs text-indigo-600 font-bold flex items-center gap-1">
                          상세히 보기 ❯
                        </span>
                        {email.Attachments?.length > 0 && (
                          <span className="text-xs text-slate-500 font-semibold flex items-center gap-1">
                            <Paperclip className="w-3.5 h-3.5 text-indigo-500" />
                            첨부 {email.Attachments.length}개
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Page Number Pagination Bar for PC */}
                {filteredEmails.length > 0 && (
                  <div className="p-3 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600 font-medium">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-700">
                        총 <span className="text-indigo-600 font-extrabold">{filteredEmails.length}</span>건
                      </span>
                      <span className="text-slate-300">|</span>
                      <span className="text-slate-500">
                        {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredEmails.length)}건 표시
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                        className="px-2 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer font-bold transition-all shadow-2xs"
                        title="이전 페이지"
                      >
                        이전
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) {
                            acc.push('ellipsis-' + p);
                          }
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((item, idx) => {
                          if (typeof item === 'string') {
                            return <span key={idx} className="px-1 text-slate-400">...</span>;
                          }
                          return (
                            <button
                              key={item}
                              type="button"
                              onClick={() => setCurrentPage(item)}
                              className={`w-7 h-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                currentPage === item
                                  ? 'bg-[#5C5FEF] text-white shadow-sm'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                              }`}
                            >
                              {item}
                            </button>
                          );
                        })}

                      <button
                        type="button"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                        className="px-2 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer font-bold transition-all shadow-2xs"
                        title="다음 페이지"
                      >
                        다음
                      </button>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] text-slate-400">보기:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-700 font-bold focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer shadow-2xs"
                      >
                        <option value={20}>20개씩</option>
                        <option value={25}>25개씩</option>
                        <option value={50}>50개씩</option>
                        <option value={100}>100개씩</option>
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Resizer Divider 2 (List <-> Reading Detail Pane in Split View) */}
        {mailViewLayout === 'split' && (
          <div
            onMouseDown={handleListMouseDown}
            className="hidden lg:flex w-3.5 -mx-1 items-center justify-center cursor-col-resize z-20 group select-none py-6 self-stretch hover:bg-indigo-50/50 transition-colors rounded-lg"
            title="마우스로 좌우 드래그하여 메일 목록 너비 조절"
          >
            <div className={`w-1 h-14 rounded-full transition-all ${
              isDraggingList ? 'bg-indigo-600 h-20 w-1.5' : 'bg-slate-300 group-hover:bg-indigo-400'
            }`} />
          </div>
        )}

        {/* 3. Right Email Detail Pane (Full Page or Split View) */}
        {((mailViewLayout === 'full' && selectedEmail) || mailViewLayout === 'split') && (
          <div className={`${
            mailViewLayout === 'full' 
              ? 'flex-1 w-full' 
              : 'hidden lg:flex flex-1 min-w-[320px]'
          } bg-white border border-slate-200 rounded-2xl flex-col overflow-y-auto shadow-xs transition-all`}>
            {selectedEmail ? (
              <div className="p-6 md:p-8 space-y-6 text-left">
                {/* Detail Navigation Bar (Back to List, Prev/Next, Layout Toggle) */}
                <div className="flex items-center justify-between gap-3 pb-4 border-b border-slate-100 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setSelectedEmail(null)}
                      className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                      title="메일 목록으로 돌아가기"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>목록으로</span>
                    </button>

                    {/* Prev / Next Mail Navigation */}
                    <div className="flex items-center bg-slate-100 rounded-xl p-0.5 border border-slate-200 text-xs font-bold shadow-2xs">
                      <button
                        type="button"
                        disabled={currentEmailIndex <= 0}
                        onClick={handlePrevEmail}
                        className="px-2.5 py-1.5 rounded-lg text-slate-700 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer flex items-center gap-1 transition-all"
                        title="이전 메일"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        <span>이전</span>
                      </button>
                      <span className="text-[11px] text-slate-500 px-2 font-mono font-semibold">
                        {currentEmailIndex >= 0 ? `${currentEmailIndex + 1} / ${filteredEmails.length}` : ''}
                      </span>
                      <button
                        type="button"
                        disabled={currentEmailIndex < 0 || currentEmailIndex >= filteredEmails.length - 1}
                        onClick={handleNextEmail}
                        className="px-2.5 py-1.5 rounded-lg text-slate-700 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer flex items-center gap-1 transition-all"
                        title="다음 메일"
                      >
                        <span>다음</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Layout Mode Toggle */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setMailViewLayout(prev => prev === 'full' ? 'split' : 'full')}
                      className={`hidden lg:flex px-2.5 py-1.5 rounded-xl text-xs font-bold border transition-all items-center gap-1.5 cursor-pointer shadow-2xs ${
                        mailViewLayout === 'full'
                          ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                          : 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                      }`}
                      title={mailViewLayout === 'full' ? '현재 전체화면 모드 (클릭 시 2분할창 모드로 전환)' : '현재 분할창 모드 (클릭 시 전체화면 모드로 전환)'}
                    >
                      <Layout className="w-3.5 h-3.5" />
                      <span>{mailViewLayout === 'full' ? '전체화면' : '분할창'}</span>
                    </button>
                  </div>
                </div>
              {/* Header info */}
              <div className="border-b border-slate-100 pb-6 space-y-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                      onClick={() => toggleStarEmail(selectedEmail.EmailID)}
                      className="text-2xl cursor-pointer hover:scale-125 transition-transform flex-shrink-0"
                      title="중요메일 토글"
                    >
                      {starredEmailIds.includes(selectedEmail.EmailID) ? (
                        <span className="text-amber-500">★</span>
                      ) : (
                        <span className="text-slate-300 hover:text-amber-400">☆</span>
                      )}
                    </button>
                    <h1 className="text-xl md:text-2xl font-bold text-slate-900 break-words">{selectedEmail.Subject}</h1>
                  </div>

                  {/* Action Toolbar on Desktop Detail Pane (Reply, Reply All, Forward, Read/Unread, Spam, Delete) */}
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    {/* Reply button */}
                    <button
                      type="button"
                      onClick={() => handleReply(selectedEmail)}
                      className="px-2.5 py-1.5 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      title="발신자에게 답장"
                    >
                      <CornerUpLeft className="w-3.5 h-3.5" />
                      <span>답장</span>
                    </button>

                    {/* Reply All button */}
                    <button
                      type="button"
                      onClick={() => handleReplyAll(selectedEmail)}
                      className="px-2.5 py-1.5 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      title="전체 수신자에게 전체 답장"
                    >
                      <ReplyAll className="w-3.5 h-3.5" />
                      <span>전체답장</span>
                    </button>

                    {/* Forward button */}
                    <button
                      type="button"
                      onClick={() => handleForward(selectedEmail)}
                      className="px-2.5 py-1.5 text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      title="메일 전달"
                    >
                      <CornerUpRight className="w-3.5 h-3.5" />
                      <span>전달</span>
                    </button>

                    {/* Read / Unread toggle */}
                    <button
                      type="button"
                      onClick={() => markEmailsRead([selectedEmail.EmailID], !selectedEmail.IsRead)}
                      className="p-1.5 text-slate-600 hover:text-indigo-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      title={selectedEmail.IsRead ? '안읽음으로 표시' : '읽음으로 표시'}
                    >
                      {selectedEmail.IsRead ? <Mail className="w-3.5 h-3.5" /> : <MailOpen className="w-3.5 h-3.5" />}
                    </button>

                    {/* Spam & Block / Restore button (Requirement 3) */}
                    {isSpamEmail(selectedEmail) ? (
                      <button
                        type="button"
                        onClick={() => handleSingleRestoreSpam(selectedEmail)}
                        className="px-2.5 py-1.5 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-all"
                        title="스팸 차단 해제 및 받은메일함으로 복원"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>복원</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSingleSpam(selectedEmail)}
                        className="px-2.5 py-1.5 text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-all"
                        title="스팸 신고 및 발신자 차단"
                      >
                        <Ban className="w-3.5 h-3.5" />
                        <span>스팸</span>
                      </button>
                    )}

                    {/* Trash Restore button */}
                    {currentFolder === 'trash' && (
                      <button
                        type="button"
                        onClick={() => handleRestoreFromTrash([selectedEmail.EmailID])}
                        className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                        title="받은메일함으로 복원"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>복원</span>
                      </button>
                    )}

                    {/* Custom Mailbox Move Dropdown for single selected email */}
                    {currentFolder !== 'trash' && (
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            promptMoveToCustomFolder([selectedEmail.EmailID], e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="bg-white border border-slate-300 text-slate-800 rounded-xl px-2.5 py-1.5 text-xs font-bold shadow-2xs focus:outline-none cursor-pointer"
                        defaultValue=""
                      >
                        <option value="" disabled>📁 메일함 이동...</option>
                        <option value="inbox">📥 받은메일함</option>
                        {customFolders.map(f => (
                          <option key={f.id} value={f.id}>📁 {f.name}</option>
                        ))}
                      </select>
                    )}

                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (currentFolder === 'trash') {
                          if (confirm('이 메일을 영구 삭제하시겠습니까? (복구 불가)')) {
                            handlePermanentDelete([selectedEmail.EmailID]);
                          }
                        } else {
                          if (confirm('이 메일을 휴지통으로 이동하시겠습니까?')) {
                            handleMoveToTrash([selectedEmail.EmailID]);
                          }
                        }
                      }}
                      className="p-1.5 text-slate-500 hover:text-rose-600 bg-slate-100 hover:bg-rose-50 rounded-xl text-xs font-bold cursor-pointer transition-all"
                      title={currentFolder === 'trash' ? '영구 삭제' : '휴지통으로 이동'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5 text-sm text-slate-600 bg-slate-50/80 p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                  <Avatar config={getSenderAvatarConfig(selectedEmail.Sender)} size={48} className="flex-shrink-0" />
                  <div className="flex-1 space-y-1.5 text-left">
                    <p className="flex items-center gap-2">
                      <span className="text-slate-400 font-medium w-20">보낸 사람:</span> 
                      <strong className="text-slate-900">{selectedEmail.Sender}</strong>
                      {isExternalSender && (
                        <button
                          onClick={handleAddToAddressBook}
                          className="px-2.5 py-1 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold cursor-pointer transition-all ml-2"
                        >
                          주소록 등록
                        </button>
                      )}
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="text-slate-400 font-medium w-20">받는 사람:</span> 
                      <span className="text-slate-800 font-medium">{selectedEmail.Recipient}</span>
                      {getGroupMailInfo(selectedEmail) && (
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 inline-flex items-center gap-1 ml-2">
                          👥 대표 그룹 메일({getGroupMailInfo(selectedEmail).Description || getGroupMailInfo(selectedEmail).GroupEmail}) 담당자 수신
                        </span>
                      )}
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="text-slate-400 font-medium w-20">보낸 일시:</span> 
                      <span className="text-slate-800 font-medium font-mono">{formatMailDateTime(selectedEmail.Timestamp || selectedEmail.DeliveredAt || selectedEmail.Date)}</span>
                    </p>
                  </div>
                </div>

                {/* Read Receipt Status Banner for Sent emails (Requirement 2) */}
                {(currentFolder === 'sent' || selectedEmail.Sender === currentUser.Email) && (
                  <div className={`p-4 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${
                    selectedEmail.IsRecipientRead 
                      ? 'bg-emerald-50/80 border-emerald-200 text-emerald-950' 
                      : 'bg-slate-50 border-slate-200 text-slate-800'
                  }`}>
                    <div className="flex items-center gap-3 text-xs md:text-sm">
                      {selectedEmail.IsRecipientRead ? (
                        <>
                          <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                            <CheckCheck className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-bold flex items-center gap-2">
                              <span>수신 확인 완료 (상대방이 메일을 열람했습니다)</span>
                              <span className="px-2 py-0.5 rounded-md bg-emerald-200/60 text-emerald-800 font-extrabold text-[11px]">
                                열람 완료
                              </span>
                            </div>
                            <span className="text-xs text-emerald-800/80 font-medium block mt-0.5">
                              열람 일시: {selectedEmail.ReadAt || '2026-08-15 18:30'} (수신자: {selectedEmail.Recipient})
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 rounded-xl bg-slate-300 text-slate-700 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-bold flex items-center gap-2">
                              <span>수신 확인 대기 중</span>
                              <span className="px-2 py-0.5 rounded-md bg-slate-200 text-slate-700 font-bold text-[11px]">
                                미열람
                              </span>
                            </div>
                            <span className="text-xs text-slate-500 font-medium block mt-0.5">
                              수신자가 아직 메일을 열람하지 않았습니다. (정상 발송 도달됨)
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowReadReceiptModal(true)}
                      className="px-3.5 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-xl text-xs font-bold shadow-2xs transition-all cursor-pointer flex-shrink-0"
                    >
                      수신확인 상세 정보
                    </button>
                  </div>
                )}
              </div>

              {/* Email body with rich responsive HTML rendering */}
              <div className="py-4 text-slate-900 leading-relaxed text-sm md:text-base bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs overflow-x-auto">
                {selectedEmail.BodyHTML && selectedEmail.BodyHTML.trim().startsWith('<') ? (
                  <div 
                    className="mail-html-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.BodyHTML) }} 
                  />
                ) : (
                  <div className="whitespace-pre-wrap font-sans text-slate-800 leading-relaxed">
                    {selectedEmail.BodyText || selectedEmail.BodyHTML || '(본문 내용이 없습니다.)'}
                  </div>
                )}
              </div>

              {/* Attachments Section with REAL Download (Requirement 2) */}
              {selectedEmail.Attachments?.length > 0 && (
                <div className="border-t border-slate-100 pt-6">
                  <span className="text-sm font-bold text-slate-700 uppercase tracking-wider block mb-3">
                    첨부파일 목록 ({selectedEmail.Attachments.length})
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedEmail.Attachments.map((att, idx) => {
                      const filename = typeof att === 'string' ? att.split('/').pop() : att.name;
                      return (
                        <div key={idx} className="bg-slate-50 border border-slate-200 p-4 rounded-2xl flex items-center justify-between hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3 truncate">
                            <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                            <span className="text-sm text-slate-900 truncate font-semibold">{filename}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => {
                                const cleanEmailId = selectedEmail?.EmailID ? selectedEmail.EmailID.replace(/-inbox.*/, '') : '';
                                let rawKey = typeof att === 'string' 
                                  ? (att.includes('/') ? att : `attachments/${cleanEmailId}/${att}`)
                                  : (att.s3Key || att.key || (cleanEmailId ? `attachments/${cleanEmailId}/${att.name || att.FileName}` : null));
                                let s3Key = rawKey;
                                if (cleanEmailId && s3Key && (s3Key.includes('temp-') || !s3Key.startsWith(`attachments/${cleanEmailId}/`))) {
                                  const fn = filename || (typeof att === 'string' ? att.split('/').pop() : (att.name || att.FileName || 'file'));
                                  s3Key = `attachments/${cleanEmailId}/${fn}`;
                                }
                                const dataUrl = typeof att === 'object' ? (att.dataUrl || att.url) : (typeof att === 'string' && (att.startsWith('data:') || att.startsWith('http') || att.startsWith('blob:')) ? att : null);
                                setPreviewAttachment({
                                  name: filename,
                                  s3Key: s3Key,
                                  dataUrl: dataUrl,
                                  size: att.size || 0,
                                  type: att.type || '',
                                  email: selectedEmail
                                });
                              }}
                              className="text-xs text-slate-700 hover:text-indigo-600 flex items-center gap-1 border border-slate-300 bg-white hover:bg-slate-50 px-3 py-1.5 rounded-xl transition-all cursor-pointer font-bold shadow-2xs"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              미리보기
                            </button>
                            <button
                              onClick={() => handleDownloadAttachment(filename)}
                              className="text-xs text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1 px-3 py-1.5 rounded-xl transition-all cursor-pointer font-bold shadow-sm"
                            >
                              <Download className="w-3.5 h-3.5" />
                              다운로드
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-center items-center text-slate-400 space-y-3 p-8">
              <Mail className="w-16 h-16 stroke-1 text-slate-300 animate-pulse" />
              <p className="text-sm font-medium">열람할 메일을 목록에서 선택해 주세요.</p>
            </div>
          )}
        </div>
      )}
      </>
      )}
      </div>

      {/* 4. Mobile Mail Detail Popup Modal (Requirement 1 & 4) */}
      {isMobileDetailOpen && selectedEmail && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4 lg:hidden">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col text-left">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <button
                onClick={() => setIsMobileDetailOpen(false)}
                className="flex items-center gap-1.5 text-sm font-bold text-slate-700 hover:text-slate-900 cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>목록으로</span>
              </button>
              <span className="text-sm font-bold text-slate-800 truncate max-w-[180px]">
                {selectedEmail.Subject}
              </span>
              <button
                onClick={() => setIsMobileDetailOpen(false)}
                className="p-1 rounded-xl text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 overflow-y-auto space-y-5 flex-1">
              <div className="space-y-3 pb-3 border-b border-slate-100">
                <h2 className="text-xl font-bold text-slate-900">{selectedEmail.Subject}</h2>
                <div className="space-y-1.5 text-xs sm:text-sm text-slate-600 bg-slate-50 p-3.5 rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-slate-400">보낸 사람: </span>
                      <strong className="text-slate-900">{selectedEmail.Sender}</strong>
                    </div>
                    {isExternalSender && (
                      <button
                        onClick={handleAddToAddressBook}
                        className="px-2 py-0.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg text-xs font-bold cursor-pointer"
                      >
                        주소록 등록
                      </button>
                    )}
                  </div>
                  <div>
                    <span className="text-slate-400">받는 사람: </span>
                    <span className="text-slate-800">{selectedEmail.Recipient}</span>
                  </div>
                  <div className="text-xs text-slate-400 pt-1">
                    {new Date(selectedEmail.Timestamp).toLocaleString()}
                  </div>
                </div>

                {/* Read Receipt Status for Mobile (Requirement 2) */}
                {(currentFolder === 'sent' || selectedEmail.Sender === currentUser.Email) && (
                  <div className={`p-3 rounded-2xl border text-xs flex items-center justify-between ${
                    selectedEmail.IsRecipientRead 
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900 font-bold' 
                      : 'bg-slate-50 border-slate-200 text-slate-700 font-medium'
                  }`}>
                    <div className="flex items-center gap-2">
                      {selectedEmail.IsRecipientRead ? (
                        <CheckCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      ) : (
                        <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      )}
                      <span>
                        {selectedEmail.IsRecipientRead 
                          ? `수신확인: 열람완료 (${selectedEmail.ReadAt || '2026-08-15 18:30'})` 
                          : '수신확인: 미열람 (발송완료)'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowReadReceiptModal(true)}
                      className="px-2.5 py-1 bg-white border border-slate-300 rounded-lg text-[11px] font-bold text-slate-800 shadow-2xs"
                    >
                      상세
                    </button>
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="text-slate-900 text-sm leading-relaxed overflow-x-auto">
                {selectedEmail.BodyHTML && selectedEmail.BodyHTML.trim().startsWith('<') ? (
                  <div 
                    className="mail-html-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.BodyHTML) }} 
                  />
                ) : (
                  <div className="whitespace-pre-wrap font-sans text-slate-800 leading-relaxed">
                    {selectedEmail.BodyText || selectedEmail.BodyHTML || '(본문 내용이 없습니다.)'}
                  </div>
                )}
              </div>

              {/* Attachments for mobile */}
              {selectedEmail.Attachments?.length > 0 && (
                <div className="pt-4 border-t border-slate-100 space-y-2.5">
                  <span className="text-sm font-bold text-slate-700">첨부파일 ({selectedEmail.Attachments.length})</span>
                  {selectedEmail.Attachments.map((att, idx) => {
                    const filename = typeof att === 'string' ? att.split('/').pop() : att.name;
                    return (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs sm:text-sm">
                        <span className="truncate font-semibold text-slate-800">{filename}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const cleanEmailId = selectedEmail?.EmailID ? selectedEmail.EmailID.replace(/-inbox.*/, '') : '';
                              let rawKey = typeof att === 'string' 
                                ? (att.includes('/') ? att : `attachments/${cleanEmailId}/${att}`)
                                : (att.s3Key || att.key || (cleanEmailId ? `attachments/${cleanEmailId}/${att.name || att.FileName}` : null));
                              let s3Key = rawKey;
                              if (cleanEmailId && s3Key && (s3Key.includes('temp-') || !s3Key.startsWith(`attachments/${cleanEmailId}/`))) {
                                const fn = filename || (typeof att === 'string' ? att.split('/').pop() : (att.name || att.FileName || 'file'));
                                s3Key = `attachments/${cleanEmailId}/${fn}`;
                              }
                              const dataUrl = typeof att === 'object' ? (att.dataUrl || att.url) : (typeof att === 'string' && (att.startsWith('data:') || att.startsWith('http') || att.startsWith('blob:')) ? att : null);
                              setPreviewAttachment({
                                name: filename,
                                s3Key: s3Key,
                                dataUrl: dataUrl,
                                size: att.size || 0,
                                type: att.type || '',
                                email: selectedEmail
                              });
                            }}
                            className="px-2.5 py-1.5 bg-white border border-slate-300 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 cursor-pointer"
                          >
                            미리보기
                          </button>
                          <button
                            onClick={() => handleDownloadAttachment(filename)}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-bold"
                          >
                            다운로드
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer (Reply, Reply All, Forward, Close) */}
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileDetailOpen(false);
                    handleReply(selectedEmail);
                  }}
                  className="px-3 py-2 bg-indigo-50 text-indigo-700 font-bold text-xs rounded-xl flex items-center gap-1"
                >
                  <CornerUpLeft className="w-3.5 h-3.5" />
                  <span>답장</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileDetailOpen(false);
                    handleReplyAll(selectedEmail);
                  }}
                  className="px-3 py-2 bg-indigo-50 text-indigo-700 font-bold text-xs rounded-xl flex items-center gap-1"
                >
                  <ReplyAll className="w-3.5 h-3.5" />
                  <span>전체답장</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileDetailOpen(false);
                    handleForward(selectedEmail);
                  }}
                  className="px-3 py-2 bg-slate-200 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-1"
                >
                  <CornerUpRight className="w-3.5 h-3.5" />
                  <span>전달</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setIsMobileDetailOpen(false)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold text-xs rounded-xl cursor-pointer"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. Universal Multi-format Document Preview Modal (Requirement 4) */}
      {previewAttachment && (
        <DocumentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          onDownload={(fn) => handleDownloadAttachment(fn)}
        />
      )}

      {/* 6. Compose Preview Modal */}
      {showComposePreview && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-2xl w-full max-h-[85vh] shadow-2xl flex flex-col space-y-4 text-left animate-in fade-in zoom-in-95 duration-150">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-indigo-600" />
                <h3 className="text-base font-bold text-slate-900">발송 메일 미리보기</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowComposePreview(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-1.5 text-xs text-slate-600 bg-slate-50 p-4 rounded-xl border border-slate-200 font-sans">
              <div><strong>보내는 사람:</strong> {composeFrom || currentUser?.Email}</div>
              <div><strong>받는 사람:</strong> {composeTo || '(미지정)'}</div>
              {composeCc && <div><strong>참조:</strong> {composeCc}</div>}
              <div><strong>제목:</strong> {isImportant && !composeSubject.startsWith('[중요]') ? `[중요] ${composeSubject}` : composeSubject || '(제목 없음)'}</div>
              {attachedFiles.length > 0 && (
                <div><strong>첨부파일 ({attachedFiles.length}개):</strong> {attachedFiles.map(f => f.name).join(', ')}</div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-white border border-slate-200 rounded-xl max-h-[45vh] text-slate-900 text-sm leading-relaxed">
              <div
                className="mail-html-body"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(composeBody || '<p>(본문 없음)</p>') }}
              />
              <div className="mt-4 pt-4 border-t border-slate-100 text-xs">
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(getUserSignature(currentUser)) }} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowComposePreview(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs cursor-pointer"
              >
                닫기
              </button>
              <button
                type="button"
                disabled={isSending || attachedFiles.some(f => f.isUploading)}
                onClick={() => {
                  if (attachedFiles.some(f => f.isUploading)) {
                    alert('첨부파일이 아직 업로드 중입니다.\n업로드가 완료된 후 발송해 주세요.');
                    return;
                  }
                  setShowComposePreview(false);
                  handleSend();
                }}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs cursor-pointer shadow-xs transition-all active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {attachedFiles.some(f => f.isUploading) ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : null}
                <span>
                  {attachedFiles.some(f => f.isUploading) ? '파일 업로드 중...' : '이대로 보내기'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}


      {/* 7. Read Receipt Detail Modal (Requirement 2) */}
      {showReadReceiptModal && selectedEmail && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-left animate-in fade-in zoom-in-95 duration-150">
            <div className="flex justify-between items-center pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                  <CheckCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">메일 실시간 수신 확인 상세</h3>
                  <span className="text-[11px] text-slate-400 font-medium">AWS SES & 클로우플레이크 메일 추적 시스템</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowReadReceiptModal(false)}
                className="p-1 rounded-xl text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Mail Info Box */}
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-1.5 font-medium">
                <div className="flex justify-between">
                  <span className="text-slate-400">메일 제목:</span>
                  <span className="font-bold text-slate-900 truncate max-w-[240px]">{selectedEmail.Subject}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">수신 대상자:</span>
                  <span className="font-bold text-indigo-600">{selectedEmail.Recipient}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">발송 일시:</span>
                  <span className="text-slate-700">{new Date(selectedEmail.Timestamp).toLocaleString()}</span>
                </div>
              </div>

              {/* Delivery & Read Tracking Timeline */}
              <div className="space-y-3 pt-1">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">전송 및 열람 타임라인</span>

                <div className="space-y-3 relative before:absolute before:inset-0 before:left-3.5 before:w-0.5 before:bg-slate-200 before:z-0">
                  {/* Step 1: Mail Dispatched */}
                  <div className="relative z-10 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 bg-white border border-slate-200 p-2.5 rounded-xl shadow-2xs">
                      <div className="flex justify-between items-center">
                        <strong className="text-slate-900 font-bold">1. 발송 완료 (Clouflake Webmail)</strong>
                        <span className="text-[10px] text-slate-400 font-mono">완료</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">사용자 클라이언트에서 AWS SES 발송 엔진으로 메일 전달 완료</p>
                    </div>
                  </div>

                  {/* Step 2: SES Gateway Relay */}
                  <div className="relative z-10 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 bg-white border border-slate-200 p-2.5 rounded-xl shadow-2xs">
                      <div className="flex justify-between items-center">
                        <strong className="text-slate-900 font-bold">2. 수신 서버 도달 (Delivered)</strong>
                        <span className="text-[10px] text-slate-400 font-mono">완료</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">수신 메일서버(MX) 응답 수신 및 메일함 안착 완료</p>
                    </div>
                  </div>

                  {/* Step 3: Recipient Open / Read */}
                  <div className="relative z-10 flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 shadow-xs ${
                      !isInternalRecipient(selectedEmail.Recipient)
                        ? 'bg-slate-300 text-slate-500'
                        : selectedEmail.IsRecipientRead ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-600'
                    }`}>
                      {!isInternalRecipient(selectedEmail.Recipient) ? <Clock className="w-4 h-4 text-slate-400" /> : selectedEmail.IsRecipientRead ? <CheckCheck className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </div>
                    <div className={`flex-1 border p-2.5 rounded-xl shadow-2xs ${
                      !isInternalRecipient(selectedEmail.Recipient)
                        ? 'bg-slate-50 border-slate-200 text-slate-500'
                        : selectedEmail.IsRecipientRead 
                          ? 'bg-emerald-50/70 border-emerald-200 text-emerald-950' 
                          : 'bg-white border-slate-200 text-slate-700'
                    }`}>
                      <div className="flex justify-between items-center">
                        <strong className="font-bold">3. 수신자 열람 상태</strong>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          !isInternalRecipient(selectedEmail.Recipient)
                            ? 'bg-slate-200 text-slate-600'
                            : selectedEmail.IsRecipientRead ? 'bg-emerald-200/70 text-emerald-900' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {!isInternalRecipient(selectedEmail.Recipient) ? '확인 불가' : selectedEmail.IsRecipientRead ? '열람 완료' : '미열람'}
                        </span>
                      </div>
                      <p className="text-[11px] mt-0.5 leading-relaxed">
                        {!isInternalRecipient(selectedEmail.Recipient)
                          ? '외부 메일 수신처(clouflake.com 외부 도메인)로 전송된 메일은 상대방의 실제 열람 여부(수신확인)를 동적으로 추적할 수 없습니다.'
                          : selectedEmail.IsRecipientRead 
                            ? `상대방이 ${selectedEmail.ReadAt || '2026-08-15 18:30'}에 메일을 확인하였습니다.` 
                            : '수신자가 아직 메일을 열람하지 않았습니다. 상대방이 메일을 열면 자동으로 상태가 업데이트됩니다.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setShowReadReceiptModal(false)}
                className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold text-xs rounded-xl cursor-pointer"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 8. Mail Backup Restore Modal (Requirement 5) */}
      {(isRestoringBackup || backupRestoreResult) && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-left animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
              <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                <PackageOpen className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {isRestoringBackup ? '메일 백업 아카이브 복구 중...' : '메일 복구 완료'}
                </h3>
                <span className="text-[11px] text-slate-400 font-medium">ZIP 압축 해제 및 EML 메일 데이터베이스 복원</span>
              </div>
            </div>

            {isRestoringBackup ? (
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-bold text-slate-700">
                    <span>압축 파일 및 .eml 파싱 진행률</span>
                    <span className="text-indigo-600 font-mono">{backupRestoreProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(backupRestoreProgress, 5)}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center animate-pulse">
                  백업 파일 내부의 list.html 및 messages 폴더 내 메일들을 가져오는 중입니다...
                </p>
              </div>
            ) : backupRestoreResult && (
              <div className="space-y-4 py-2">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3 text-xs">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <strong className="text-emerald-950 font-bold block">
                      총 {backupRestoreResult.count}건의 메일이 성공적으로 복구되었습니다!
                    </strong>
                    <span className="text-emerald-800 text-[11px] mt-0.5 block">
                      수신함 및 보낸편지함 목록에 EML 메일들이 즉시 반영되었습니다.
                    </span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setBackupRestoreResult(null)}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer"
                  >
                    확인 및 메일함 보기
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Move Mail Options Modal */}
      {moveOptionModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-6 text-left space-y-4 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-lg font-bold flex-shrink-0">
                📁
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">메일함 이동 옵션 선택</h3>
                <p className="text-xs text-slate-500">
                  선택한 메일을 <strong className="text-indigo-600 font-bold">'{moveOptionModal.targetFolderName}'</strong>(으)로 이동합니다.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1.5 text-xs text-slate-700">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-500">발신자</span>
                <span className="font-semibold text-slate-900 truncate max-w-[240px]">{moveOptionModal.sender}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-500">동일 발신자 메일 수</span>
                <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">
                  총 {moveOptionModal.sameSenderCount}개
                </span>
              </div>
            </div>

            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={() => executeMoveToCustomFolder(moveOptionModal.emailIds, moveOptionModal.targetFolderId, false)}
                className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 font-bold rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all shadow-2xs"
              >
                <span>이 메일만 이동</span>
                <span className="text-[11px] text-slate-400 font-normal">선택한 메일만 이동 ❯</span>
              </button>

              <button
                type="button"
                onClick={() => executeMoveToCustomFolder(moveOptionModal.emailIds, moveOptionModal.targetFolderId, true)}
                className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all shadow-2xs"
              >
                <span>동일 발신자의 모든 메일 함께 이동 ({moveOptionModal.sameSenderCount}개)</span>
                <span className="text-[11px] text-indigo-100 font-normal">향후 수신 포함 ❯</span>
              </button>

              <button
                type="button"
                onClick={() => setMoveOptionModal(prev => ({ ...prev, isOpen: false }))}
                className="w-full py-2 text-slate-500 hover:text-slate-700 font-bold text-xs cursor-pointer text-center"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WebmailTab;
